import React, { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 max-w-2xl mx-auto space-y-4 border border-destructive/20 rounded bg-destructive/5 mt-12 font-sans">
          <h1 className="text-2xl font-bold text-destructive">React Rendering Error</h1>
          <p className="text-sm font-semibold text-muted-foreground">The page crashed while rendering the loaded proposal data:</p>
          <pre className="p-4 bg-muted text-xs overflow-auto rounded max-h-[300px] border font-mono whitespace-pre-wrap">
            {this.state.error?.stack || this.state.error?.message || String(this.state.error)}
          </pre>
          <button 
            type="button" 
            className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-primary/90 transition-colors"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/app-shell";
import { resolveUrl } from "@/lib/queryClient";
import { OrgChart } from "@/components/ui/org-chart";
import { useAuth } from "@/hooks/use-auth";
import { useCustomer } from "@/hooks/use-customer";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, FileText, Loader2, Send, Info, Wand2, Save, Download, FileSpreadsheet, Upload, Printer, ClipboardList, CheckCircle2, Laptop } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { apiRequest } from "@/lib/queryClient";

const POTENTIAL_PROJECTS = [
  "Intelligent HVAC control & monitoring: Automated schedules and setpoints based on occupancy and load.",
  "Ventilation variable control (hoods/laundry): Adjusting exhaust and makeup air based on actual usage.",
  "Walk-in refrigeration control and energy storage: Optimizing defrost cycles and floating head pressure.",
  "Lighting control and monitoring: Occupancy sensors, daylight harvesting, and scheduling.",
  "Electrification: Replacing fossil fuel boilers with high-efficiency heat pumps.",
  "Solar Panel installation: On-site renewable energy generation to offset grid usage.",
  "Full/Partial HVAC replacement: Upgrading end-of-life RTUs or chillers to high-efficiency models.",
  "Battery Energy Storage Systems (BESS): Peak shaving, load shifting, and demand charge management.",
  "Thermal Energy Storage (TES): Ice or chilled water storage to shift cooling loads to off-peak hours.",
  "Demand Response Integration: Automated shedding of non-critical loads during peak utility events.",
  "Variable Frequency Drives (VFDs): Optimizing motor speed for large fans and pumps to reduce energy waste.",
  "Premium Efficiency Motor Upgrades: Upgrading legacy standard motors to NEMA Premium models.",
  "Combined Heat and Power (CHP): Generating electricity on-site and capturing waste heat.",
  "Exhaust / Waste Heat Recovery: Capturing heat from processes or exhaust to pre-heat water/air.",
  "Advanced Boiler Controls & Economizers: Oxygen trim controls, condensing upgrades, and stack economizers.",
  "Building Envelope Upgrades: Improving insulation, weatherstripping, window glazing, and cool roofs.",
  "Smart Plug Load Management: Automated control to turn off unused equipment during unoccupied hours.",
  "Compressed Air System Optimization: Leak detection, VFDs, and reducing system set pressures."
];

const osemFormSchema = z.object({
  companyName: z.string().min(2, "Company name is required"),
  companyDescription: z.string().optional(),
  sustainabilityGoals: z.string().optional(),
  pastProjectsHistory: z.string().optional(),
  weeklyHours: z.coerce.number().min(20).max(40).default(20),
  onSitePercentage: z.coerce.number().min(0).max(100).default(50),
  facilities: z.array(z.object({
    name: z.string().min(1, "Facility name is required"),
    address: z.string().min(1, "Address is required"),
    equipmentProfile: z.string().min(1, "Equipment profile description is required"),
  })).min(1, "At least one facility is required"),
  businessType: z.string().optional(),
  equipmentOverview: z.string().optional(),
  softwarePlatforms: z.string().optional(),
  staffCulture: z.string().optional(),
  trainingRequirements: z.string().optional(),
  organizationSetup: z.string().optional(),
  energyManagerAgency: z.string().min(1, "Energy Manager Agency is required"),
  glidepathPlan: z.string().optional(),
  taskDeliverables: z.record(z.string()).optional(),
  electricUsageKwh: z.coerce.number().min(0, "Must be a positive number"),
  electricCost: z.coerce.number().min(0, "Must be a positive number"),
  gasUsageTherms: z.coerce.number().min(0, "Must be a positive number"),
  gasCost: z.coerce.number().min(0, "Must be a positive number"),
  electricSavingsTarget: z.coerce.number().min(0).max(100),
  fossilFuelSavingsTarget: z.coerce.number().min(0).max(100),
  executiveSponsorName: z.string().min(1, "Name is required"),
  executiveSponsorTitle: z.string().min(1, "Title is required"),
  orgChart: z.array(z.object({
    id: z.string(),
    name: z.string().min(1, "Name is required"),
    title: z.string().min(1, "Title is required"),
    managerId: z.string().min(1, "Manager is required"),
  })).default([]),
  onitExpert: z.string().optional(),
  potentialProjects: z.array(z.string()),
  targetEnergySavings: z.string().optional(),
  seapPlatformTraining: z.boolean().default(true),
  developTrainingMaterial: z.boolean().default(true),
  omProceduresSupport: z.boolean().default(true),
  trainingVideosCreation: z.string().optional(),
  additionalTasks: z.array(z.object({
    id: z.string().optional(),
    title: z.string().min(1, "Task title is required"),
    description: z.string().min(1, "Task description is required"),
    hours: z.coerce.number().min(0, "Hours must be positive"),
  })).default([]),
  hourlyRate: z.coerce.number().min(0).default(150),
  nyserdaCostSharePercentage: z.coerce.number().min(0).max(100).default(75),
  travelBudget: z.coerce.number().min(0).default(0),
  materialsBudget: z.coerce.number().min(0).default(0),
  durationMonths: z.coerce.number().min(2).max(24).default(12),
  projectSchedule: z.array(z.object({
    category: z.string().min(1, "Category name is required"),
    tasks: z.array(z.object({
      description: z.string().min(1, "Task description is required"),
      months: z.array(z.number())
    })).default([])
  })).default([]),
  budgetTasks: z.array(z.object({
    taskNumber: z.string().min(1, "Task # is required"),
    description: z.string().min(1, "Description is required"),
    roleTitle: z.string().min(1, "Role title is required"),
    rate: z.coerce.number().min(0).default(150),
    hours: z.coerce.number().min(0).default(0),
    multiplier: z.coerce.number().min(0).default(1),
    onitHours: z.coerce.number().min(0).default(0),
    onitRate: z.coerce.number().min(0).default(150),
    rotbHours: z.coerce.number().min(0).default(0),
    rotbRate: z.coerce.number().min(0).default(75),
  })).default([]),
  ecmFindings: z.array(z.object({
    ecmNumber: z.string().min(1, "ECM # is required"),
    description: z.string().min(1, "Description is required"),
    location: z.string().optional().default(""),
    quarter: z.string().optional().default(""),
    installed: z.string().optional().default("N"),
    electricSavings: z.coerce.number().default(0),
    demandSavings: z.coerce.number().default(0),
    fossilFuelSavings: z.coerce.number().default(0),
    fossilFuelSource: z.string().optional().default(""),
    costSavings: z.coerce.number().default(0),
    projectCost: z.coerce.number().default(0),
    incentiveProgram: z.string().optional().default(""),
    notes: z.string().optional().default(""),
  })).default([]),
  invoiceCycles: z.array(z.object({
    date: z.string().min(1, "Cycle date is required"),
    hours: z.array(z.object({
      taskNumber: z.string(),
      onitHours: z.coerce.number().default(0),
      rotbHours: z.coerce.number().default(0)
    })).default([])
  })).default([]),
});

// Helper for hover labels
const HelpTooltip = ({ content }: { content: string }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger type="button" tabIndex={-1}>
        <Info className="h-4 w-4 ml-2 text-muted-foreground hover:text-primary transition-colors" />
      </TooltipTrigger>
      <TooltipContent className="max-w-sm text-sm">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

type OsemFormValues = z.infer<typeof osemFormSchema>;

function ScheduleCategoryItem({ control, categoryIndex, removeCategory, durationMonths }: { control: any, categoryIndex: number, removeCategory: () => void, durationMonths: number }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `projectSchedule.${categoryIndex}.tasks`
  });

  return (
    <div className="border rounded bg-muted/20 p-4 space-y-4">
      <div className="flex justify-between items-center">
        <FormField
          control={control}
          name={`projectSchedule.${categoryIndex}.category`}
          render={({ field }) => (
            <FormItem className="flex-1 mr-4">
              <FormControl>
                <Input {...field} placeholder="e.g., Deliverable to NYSERDA 1" className="font-semibold" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="button" variant="ghost" size="icon" onClick={removeCategory} className="text-destructive h-8 w-8">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
        <div className="text-xs font-semibold text-muted-foreground uppercase">Task Description</div>
        <div className="flex gap-1 pr-10">
          {Array.from({ length: Number(durationMonths) || 12 }).map((_, i) => (
            <div key={i} className="w-8 text-center text-xs font-semibold text-muted-foreground">M{i+1}</div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {(fields || []).map((taskField, taskIndex) => (
          <div key={taskField.id} className="grid grid-cols-[1fr_auto] gap-2 items-start">
            <FormField
              control={control}
              name={`projectSchedule.${categoryIndex}.tasks.${taskIndex}.description`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Kick-Off Meeting" className="h-8 text-sm" />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex gap-1 items-center">
              <FormField
                control={control}
                name={`projectSchedule.${categoryIndex}.tasks.${taskIndex}.months`}
                render={({ field }) => (
                  <div className="flex gap-1 items-center">
                    {Array.from({ length: Number(durationMonths) || 12 }).map((_, i) => {
                      const monthNum = i + 1;
                      const isChecked = Array.isArray(field.value) && field.value.includes(monthNum);
                      return (
                        <div key={monthNum} className="w-8 flex justify-center">
                          <Checkbox
                            checked={!!isChecked}
                            onCheckedChange={(checked) => {
                              const currentVal = Array.isArray(field.value) ? field.value : [];
                              const newMonths = checked
                                ? [...currentVal, monthNum].sort((a, b) => a - b)
                                : currentVal.filter((m: number) => m !== monthNum);
                              field.onChange(newMonths);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(taskIndex)} className="h-8 w-8 text-muted-foreground">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={() => append({ description: "", months: [] })} className="text-xs mt-2">
        <Plus className="mr-2 h-3 w-3" /> Add Task
      </Button>
    </div>
  );
}

function BudgetTaskTable({ control, watch }: { control: any; watch: any }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "budgetTasks"
  });

  const allTasks = watch("budgetTasks") || [];
  const totalCost = allTasks.reduce((acc: number, task: any) => {
    const onitHrs = Number(task.onitHours) || Number(task.hours) || 0;
    const onitRt = Number(task.onitRate) || Number(task.rate) || 150;
    const rotbHrs = Number(task.rotbHours) || 0;
    const rotbRt = Number(task.rotbRate) || 75;
    const mult = Number(task.multiplier) || 1;
    return acc + ((onitHrs * onitRt) + (rotbHrs * rotbRt)) * mult;
  }, 0);

  const nyserdaShare = totalCost * 0.75;
  const customerShare = totalCost * 0.25;

  return (
    <div className="border rounded bg-muted/10">
      <div className="grid grid-cols-[3.5rem_1fr_10rem_6rem_6rem_6rem_6rem_6rem_auto] gap-2 items-center p-3 text-xs font-semibold uppercase text-muted-foreground bg-muted/30 border-b">
        <div>Task</div>
        <div>Description</div>
        <div>Role</div>
        <div className="text-center">ONIT Rate ($)</div>
        <div className="text-center">ONIT Hours</div>
        <div className="text-center">ROTB Rate ($)</div>
        <div className="text-center">ROTB Hours</div>
        <div className="text-right font-bold">Total ($)</div>
        <div className="w-8"></div>
      </div>
      
      <div className="p-2 space-y-2">
        {fields.map((field, index) => {
          const task = allTasks[index] || {};
          const onitHrs = Number(task.onitHours) || Number(task.hours) || 0;
          const onitRt = Number(task.onitRate) || Number(task.rate) || 150;
          const rotbHrs = Number(task.rotbHours) || 0;
          const rotbRt = Number(task.rotbRate) || 75;
          const rowTotal = (onitHrs * onitRt) + (rotbHrs * rotbRt);

          return (
            <div key={field.id} className="grid grid-cols-[3.5rem_1fr_10rem_6rem_6rem_6rem_6rem_6rem_auto] gap-2 items-start py-1">
              <FormField control={control} name={`budgetTasks.${index}.taskNumber`} render={({ field }) => (
                <Input {...field} placeholder="1a" className="h-8 text-sm px-2" />
              )} />
              <FormField control={control} name={`budgetTasks.${index}.description`} render={({ field }) => (
                <Input {...field} placeholder="Desc..." className="h-8 text-sm px-2" />
              )} />
              <FormField control={control} name={`budgetTasks.${index}.roleTitle`} render={({ field }) => (
                <Input {...field} placeholder="Engineer" className="h-8 text-sm px-2" />
              )} />
              <FormField control={control} name={`budgetTasks.${index}.onitRate`} render={({ field }) => (
                <Input {...field} type="number" className="h-8 text-sm px-2 text-center" />
              )} />
              <FormField control={control} name={`budgetTasks.${index}.onitHours`} render={({ field }) => (
                <Input {...field} type="number" className="h-8 text-sm px-2 text-center" />
              )} />
              <FormField control={control} name={`budgetTasks.${index}.rotbRate`} render={({ field }) => (
                <Input {...field} type="number" className="h-8 text-sm px-2 text-center" />
              )} />
              <FormField control={control} name={`budgetTasks.${index}.rotbHours`} render={({ field }) => (
                <Input {...field} type="number" className="h-8 text-sm px-2 text-center" />
              )} />
              <div className="flex items-center justify-end h-8 text-sm font-semibold pr-2">
                ${rowTotal.toLocaleString()}
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)} className="h-8 w-8 text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
      
      <div className="flex justify-between items-center p-3 border-t bg-muted/20 text-sm">
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={() => append({ taskNumber: `${fields.length + 1}`, description: "", roleTitle: "ONIT Engineer", rate: 150, hours: 0, multiplier: 1, onitHours: 0, onitRate: 150, rotbHours: 0, rotbRate: 75 })}
        >
          <Plus className="h-4 w-4 mr-2" /> Add Budget Row
        </Button>
        <div className="flex gap-4 font-semibold text-right">
          <div>
            <span className="text-muted-foreground mr-1 text-xs">TOTAL:</span>
            <span>${totalCost.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-emerald-600 mr-1 text-xs">NYSERDA (75%):</span>
            <span className="text-emerald-600">${nyserdaShare.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-blue-600 mr-1 text-xs">CUSTOMER (25%):</span>
            <span className="text-blue-600">${customerShare.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function EcmFindingsTable({ control, watch }: { control: any; watch: any }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "ecmFindings"
  });

  const allEcm = watch("ecmFindings") || [];
  
  return (
    <div className="border rounded bg-muted/10">
      <div className="grid grid-cols-[4rem_1fr_6rem_4rem_5rem_6rem_6rem_6rem_4rem] gap-2 items-center p-3 text-xs font-semibold uppercase text-muted-foreground bg-muted/30 border-b">
        <div>ECM #</div>
        <div>Description</div>
        <div>Location</div>
        <div className="text-center">Installed</div>
        <div className="text-right">kWh Sav.</div>
        <div className="text-right">MMBtu Sav.</div>
        <div className="text-right">Cost Sav.</div>
        <div className="text-right">Proj. Cost</div>
        <div className="w-8"></div>
      </div>
      
      <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
        {fields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-[4rem_1fr_6rem_4rem_5rem_6rem_6rem_6rem_4rem] gap-2 items-start py-1">
            <FormField control={control} name={`ecmFindings.${index}.ecmNumber`} render={({ field }) => (
              <Input {...field} placeholder="1.0" className="h-8 text-sm px-2" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.description`} render={({ field }) => (
              <Input {...field} placeholder="Description" className="h-8 text-sm px-2" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.location`} render={({ field }) => (
              <Input {...field} placeholder="Roof" className="h-8 text-sm px-2" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.installed`} render={({ field }) => (
              <Input {...field} placeholder="Y/N" className="h-8 text-sm px-2 text-center" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.electricSavings`} render={({ field }) => (
              <Input {...field} type="number" className="h-8 text-sm px-2 text-right" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.fossilFuelSavings`} render={({ field }) => (
              <Input {...field} type="number" className="h-8 text-sm px-2 text-right" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.costSavings`} render={({ field }) => (
              <Input {...field} type="number" className="h-8 text-sm px-2 text-right" />
            )} />
            <FormField control={control} name={`ecmFindings.${index}.projectCost`} render={({ field }) => (
              <Input {...field} type="number" className="h-8 text-sm px-2 text-right" />
            )} />
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)} className="h-8 w-8 text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      
      <div className="flex justify-between items-center p-3 border-t bg-muted/20">
        <Button 
          type="button" 
          variant="secondary" 
          size="sm" 
          onClick={() => append({ ecmNumber: `${allEcm.length + 1}.0`, description: "", location: "", quarter: "Q1", installed: "N", electricSavings: 0, demandSavings: 0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 0, projectCost: 0, incentiveProgram: "", notes: "" })}
        >
          <Plus className="mr-2 h-4 w-4" /> Add ECM Finding
        </Button>
      </div>
    </div>
  );
}

function FacilityAnalysesManager({ proposalId, ecmFindings, onUpdateFindings }: { proposalId: number; ecmFindings: any[]; onUpdateFindings: (newFindings: any[]) => void }) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { activeCustomerId } = useCustomer();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingAnalysis, setEditingAnalysis] = useState<any | null>(null);
  const [selectedTask, setSelectedTask] = useState<string>("");
  
  // Form fields for create/edit analysis
  const [name, setName] = useState("");
  const [ecmType, setEcmType] = useState("boiler");
  const [description, setDescription] = useState("");
  const [projectCost, setProjectCost] = useState("0");
  const [incentive, setIncentive] = useState("0");
  const [electricSavingsKwh, setElectricSavingsKwh] = useState("0");
  const [demandSavingsKw, setDemandSavingsKw] = useState("0");
  const [fossilFuelSavingsMmbtu, setFossilFuelSavingsMmbtu] = useState("0");
  const [fossilFuelSource, setFossilFuelSource] = useState("Natural Gas");
  const [energyCostSavings, setEnergyCostSavings] = useState("0");
  const [operationalSavings, setOperationalSavings] = useState("0");
  const [isEcm, setIsEcm] = useState(true);
  const [linkToDeve, setLinkToDeve] = useState(false);
  const [improvementType, setImprovementType] = useState("ECM");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    
    const formData = new FormData();
    formData.append("file", file);
    
    setUploading(true);
    try {
      const res = await fetch(resolveUrl(`/api/facilities/${activeCustomerId}/upload`), {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setAttachments(prev => [...prev, data.fileName]);
      toast({
        title: "File Uploaded",
        description: `Successfully uploaded ${file.name}`
      });
    } catch (err: any) {
      toast({
        title: "Upload Error",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };


  const { data: analysesData, isLoading } = useQuery({
    queryKey: [`/api/osem/${proposalId}/analyses`],
    queryFn: async () => {
      const res = await fetch(resolveUrl(`/api/osem/${proposalId}/analyses`));
      return res.json();
    },
    enabled: !!proposalId
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const method = payload.id ? "PUT" : "POST";
      const url = payload.id ? `/api/facility-analyses/${payload.id}` : "/api/facility-analyses";
      const res = await fetch(resolveUrl(url), {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, proposalId })
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to save analysis");
      }
      return res.json();
    },
    onSuccess: (savedAnalysis) => {
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${proposalId}/analyses`] });
      setIsOpen(false);
      
      const currentGeneral = analysesData?.generalAnalyses || [];
      const updatedGeneral = savedAnalysis.id 
        ? currentGeneral.map((a: any) => a.id === savedAnalysis.id ? savedAnalysis : a)
        : [...currentGeneral, savedAnalysis];
        
      const newFindings = [...ecmFindings];
      
      const tasksMap: Record<string, any[]> = {};
      updatedGeneral.forEach((a: any) => {
        if (!tasksMap[a.taskId]) tasksMap[a.taskId] = [];
        tasksMap[a.taskId].push(a);
      });
      
      Object.keys(tasksMap).forEach(tId => {
        const taskNum = tId.replace("task", "");
        // Only include ECM analyses
        const ecmOnly = tasksMap[tId].filter((a: any) => a.isEcm !== false);
        ecmOnly.forEach((analysis, idx) => {
          const ecmNum = `${taskNum}.${idx + 1}`;
          const existingIdx = newFindings.findIndex(f => f.ecmNumber === ecmNum);
          
          const findingData = {
            ecmNumber: ecmNum,
            description: analysis.name,
            location: "Facility",
            quarter: "Q4",
            installed: analysis.status === "completed" ? "Y" : "N",
            electricSavings: Number(analysis.electricSavingsKwh) || 0,
            demandSavings: Number(analysis.demandSavingsKw) || 0,
            fossilFuelSavings: Number(analysis.fossilFuelSavingsMmbtu) || 0,
            fossilFuelSource: analysis.fossilFuelSource || "Natural Gas",
            costSavings: Number(analysis.energyCostSavings) || 0,
            projectCost: Number(analysis.projectCost) || 0,
            incentiveProgram: "NYSERDA PON 3701",
            notes: analysis.description || ""
          };
          
          if (existingIdx !== -1) {
            newFindings[existingIdx] = findingData;
          } else {
            newFindings.push(findingData);
          }
        });
      });
      onUpdateFindings(newFindings);
      toast({
        title: "Analysis Saved",
        description: "Standardized ECM sheet updated and auto-fed into Appendix P."
      });
    },
    onError: (err: any) => {
      toast({
        title: "Save Failed",
        description: err.message,
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await fetch(resolveUrl(`/api/facility-analyses/${id}`), { method: "DELETE" });
      return id;
    },
    onSuccess: (deletedId) => {
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${proposalId}/analyses`] });
      
      const deletedAnalysis = (analysesData?.generalAnalyses || []).find((a: any) => a.id === deletedId);
      if (deletedAnalysis) {
        const taskId = deletedAnalysis.taskId;
        const taskNum = taskId.replace("task", "");
        const remainingFindings = ecmFindings.filter(f => !f.ecmNumber.startsWith(`${taskNum}.`));
        onUpdateFindings(remainingFindings);
      }
      
      toast({
        title: "Analysis Deleted",
        description: "Removed associated ECM finding from Appendix P."
      });
    }
  });

  const openCreateModal = (taskId: string) => {
    setSelectedTask(taskId);
    setEditingAnalysis(null);
    setName("");
    setEcmType("boiler");
    setDescription("");
    setProjectCost("0");
    setIncentive("0");
    setElectricSavingsKwh("0");
    setDemandSavingsKw("0");
    setFossilFuelSavingsMmbtu("0");
    setFossilFuelSource("Natural Gas");
    setEnergyCostSavings("0");
    setOperationalSavings("0");
    setIsEcm(true);
    setLinkToDeve(false);
    setImprovementType("ECM");
    setAttachments([]);
    setIsOpen(true);
  };

  const openEditModal = (analysis: any) => {
    setEditingAnalysis(analysis);
    setSelectedTask(analysis.taskId);
    setName(analysis.name);
    setEcmType(analysis.ecmType);
    setDescription(analysis.description || "");
    setProjectCost(String(analysis.projectCost || 0));
    setIncentive(String(analysis.incentive || 0));
    setElectricSavingsKwh(String(analysis.electricSavingsKwh || 0));
    setDemandSavingsKw(String(analysis.demandSavingsKw || 0));
    setFossilFuelSavingsMmbtu(String(analysis.fossilFuelSavingsMmbtu || 0));
    setFossilFuelSource(analysis.fossilFuelSource || "Natural Gas");
    setEnergyCostSavings(String(analysis.energyCostSavings || 0));
    setOperationalSavings(String(analysis.operationalSavings || 0));
    setIsEcm(analysis.isEcm !== false);
    setLinkToDeve(analysis.linkToDeve === true);
    setImprovementType(analysis.improvementType || "ECM");
    setAttachments(analysis.attachments || []);
    setIsOpen(true);
  };

  const handleSave = () => {
    const payload = {
      id: editingAnalysis?.id,
      taskId: selectedTask,
      name,
      ecmType,
      description,
      isEcm,
      linkToDeve,
      improvementType,
      projectCost: parseFloat(projectCost) || 0,
      incentive: parseFloat(incentive) || 0,
      electricSavingsKwh: parseFloat(electricSavingsKwh) || 0,
      demandSavingsKw: parseFloat(demandSavingsKw) || 0,
      fossilFuelSavingsMmbtu: parseFloat(fossilFuelSavingsMmbtu) || 0,
      fossilFuelSource,
      energyCostSavings: parseFloat(energyCostSavings) || 0,
      operationalSavings: parseFloat(operationalSavings) || 0,
      attachments,
      simplePayback: parseFloat(energyCostSavings) > 0 ? (parseFloat(projectCost) / parseFloat(energyCostSavings)) : 0
    };
    saveMutation.mutate(payload);
  };

  const general = analysesData?.generalAnalyses || [];
  
  const standardTasks = [
    { id: "task1", number: "1", title: "Develop energy management plan" },
    { id: "task2", number: "2", title: "Develop energy management plan deliverables" },
    { id: "task3", number: "3", title: "Utility Tracking & Submetering Integration" },
    { id: "task4", number: "4", title: "Conduct walkthrough audits and reports" },
    { id: "task5", number: "5", title: "M&V Tool Development: Case studies" },
    { id: "task6", number: "6", title: "Technical feasibility studies for ECMs" },
    { id: "task7", number: "7", title: "Review O&M schedules and procedures" },
    { id: "task8", number: "8", title: "Training and employee awareness sessions" },
    { id: "task9", number: "9", title: "Scope, support, and financially analyze solar/energy storage" },
    { id: "task10", number: "10", title: "Transition & Final Transition Execution" },
    { id: "task11", number: "11", title: "Reporting: Monthly and Quarterly" }
  ];

  return (
    <Card className="mt-8 border-primary/20">
      <CardHeader className="bg-primary/5 pb-4">
        <CardTitle className="text-lg font-semibold flex items-center gap-2 text-primary">
          <ClipboardList className="h-5 w-5" />
          Facility ECM Analyses & SOW Tasks
        </CardTitle>
        <CardDescription>
          Plan, check off, and manage individual ECM analyses (Boilers, RTUs, BMS, Lighting, Pumps). Completed analyses automatically populate the NYSERDA Appendix P summary sheet.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-6 space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-6 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading analyses...</div>
        ) : (
          <div className="space-y-4">
            {standardTasks.map(task => {
              const taskGeneralAnalyses = general.filter((a: any) => a.taskId === task.id);

              return (
                <div key={task.id} className="border rounded bg-muted/5 overflow-hidden">
                  <div className="flex justify-between items-center p-3 bg-muted/20 border-b">
                    <div>
                      <span className="text-xs font-semibold text-muted-foreground uppercase mr-2">Task {task.number}</span>
                      <h4 className="inline-block text-sm font-semibold">{task.title}</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      {task.number === "3" && (
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="link" 
                          className="text-primary hover:text-primary/80 h-auto p-0 mr-2 text-xs font-medium"
                          onClick={() => setLocation("/equipment-capture")}
                        >
                          <Laptop className="h-3.5 w-3.5 mr-1" /> Equipment Capture Tool
                        </Button>
                      )}
                      {task.number === "6" && (
                        <>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="link" 
                            className="text-primary hover:text-primary/80 h-auto p-0 mr-2 text-xs font-medium"
                            onClick={() => setLocation("/analyses")}
                          >
                            <Laptop className="h-3.5 w-3.5 mr-1" /> Pump Analysis Tool
                          </Button>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="link" 
                            className="text-primary hover:text-primary/80 h-auto p-0 mr-2 text-xs font-medium"
                            onClick={() => setLocation(`/osem-onboarding/${proposalId}/lighting`)}
                          >
                            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Lighting Auditor
                          </Button>
                        </>
                      )}
                      <Button type="button" size="sm" variant="outline" onClick={() => openCreateModal(task.id)}>
                        <Plus className="h-4 w-4 mr-1" /> Add ECM Analysis
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-3 space-y-3">
                    {taskGeneralAnalyses.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">No facility ECM analyses associated with this task. Click "Add ECM Analysis" to plan one.</div>
                    ) : (
                      taskGeneralAnalyses.map((analysis: any) => (
                        <Card key={analysis.id} className="bg-background shadow-none border">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <Badge variant="secondary" className="mr-2 uppercase text-[10px]">{analysis.ecmType}</Badge>
                                <span className="font-semibold text-sm">{analysis.name}</span>
                                {analysis.description && (
                                  <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap font-serif border-l-2 pl-2 border-primary/20">{analysis.description}</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button type="button" size="sm" variant="outline" onClick={() => openEditModal(analysis)}>Edit</Button>
                                <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => deleteMutation.mutate(analysis.id)}>Delete</Button>
                              </div>
                            </div>
                            
                            <div className="border rounded overflow-x-auto mt-2 bg-muted/10">
                              <table className="min-w-full text-xs font-mono">
                                <thead className="bg-muted/30">
                                  <tr>
                                    <th className="px-2 py-1 text-left">Project Cost</th>
                                    <th className="px-2 py-1 text-left">Incentive</th>
                                    <th className="px-2 py-1 text-left">Electric Savings (kWh)</th>
                                    <th className="px-2 py-1 text-left">Fuel Savings (MMBtu)</th>
                                    <th className="px-2 py-1 text-left">Energy Cost Savings</th>
                                    <th className="px-2 py-1 text-left text-blue-600">Operational Savings</th>
                                    <th className="px-2 py-1 text-left font-bold text-primary">Simple Payback</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-t">
                                    <td className="px-2 py-1">${(analysis.projectCost || 0).toLocaleString()}</td>
                                    <td className="px-2 py-1">${(analysis.incentive || 0).toLocaleString()}</td>
                                    <td className="px-2 py-1">{(analysis.electricSavingsKwh || 0).toLocaleString()}</td>
                                    <td className="px-2 py-1">{(analysis.fossilFuelSavingsMmbtu || 0).toLocaleString()} ({analysis.fossilFuelSource})</td>
                                    <td className="px-2 py-1">${(analysis.energyCostSavings || 0).toLocaleString()}/yr</td>
                                    <td className="px-2 py-1 text-blue-600">${(analysis.operationalSavings || 0).toLocaleString()}/yr</td>
                                    <td className="px-2 py-1 font-bold text-primary">
                                      {analysis.simplePayback ? `${analysis.simplePayback.toFixed(1)} yrs` : "0.0 yrs"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {analysis.attachments && analysis.attachments.length > 0 && (
                              <div className="flex flex-wrap gap-1 items-center mt-2 text-xs">
                                <span className="text-muted-foreground mr-1">Attached Files:</span>
                                {analysis.attachments.map((file: string, idx: number) => (
                                  <a 
                                    key={idx} 
                                    href={`/api/facilities/${activeCustomerId}/download/${file}`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                  >
                                    <Badge variant="outline" className="text-[10px] bg-muted/40 font-mono hover:bg-muted/80 transition-colors cursor-pointer">{file}</Badge>
                                  </a>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingAnalysis ? "Edit ECM Analysis" : "Configure Facility ECM Analysis"}</DialogTitle>
            <DialogDescription>
              Provide preamble discussion and savings outcomes. These figures will autofill the main Appendix P summary sheet.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4 text-sm">
            <div className="col-span-2 space-y-1">
              <Label>Analysis Title</Label>
              <Input placeholder="e.g. Boiler Circulation Pump VFD Upgrade" value={name} onChange={e => setName(e.target.value)} />
            </div>

            <div className="space-y-1">
              <Label>Improvement Category</Label>
              <Select 
                value={improvementType} 
                onValueChange={(val) => {
                  setImprovementType(val);
                  if (val === "ECM") {
                    setIsEcm(true);
                  } else {
                    setIsEcm(false);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ECM">ECM (Energy Conservation Measure)</SelectItem>
                  <SelectItem value="CAPEX">CAPEX (Capital Expenditure)</SelectItem>
                  <SelectItem value="OPEX">OPEX (Operational Expenditure)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-col justify-center space-y-2 border p-2 rounded bg-muted/20">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="isEcmCheckbox"
                  checked={isEcm} 
                  onChange={e => setIsEcm(e.target.checked)} 
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="isEcmCheckbox" className="cursor-pointer font-normal text-xs">This is an ECM (Feeds NYSERDA Appendix P)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="linkToDeveCheckbox"
                  checked={linkToDeve} 
                  onChange={e => setLinkToDeve(e.target.checked)} 
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="linkToDeveCheckbox" className="cursor-pointer font-normal text-xs text-blue-600 font-semibold">Link to DEVE Portfolio</Label>
              </div>
            </div>
            
            <div className="space-y-1">
              <Label>ECM Type</Label>
              <Select value={ecmType} onValueChange={setEcmType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boiler">Boiler / Circulator</SelectItem>
                  <SelectItem value="rtu">HVAC RTU Replacement</SelectItem>
                  <SelectItem value="bms">BMS & Smart Schedules</SelectItem>
                  <SelectItem value="plug_load">Plug Load Controls</SelectItem>
                  <SelectItem value="solar">Solar PV Analysis</SelectItem>
                  <SelectItem value="lighting">Lighting & Controls</SelectItem>
                  <SelectItem value="pump">Pump Retrofit</SelectItem>
                  <SelectItem value="custom">Other / Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label>Fossil Fuel Source</Label>
              <Input value={fossilFuelSource} onChange={e => setFossilFuelSource(e.target.value)} />
            </div>

            <div className="col-span-2 space-y-1">
              <Label>Preamble / Technical Discussion (Observations & Calculations)</Label>
              <Textarea 
                placeholder="Include initial observations, measured power data, proposed control strategy..."
                className="min-h-[100px]"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            
            <div className="space-y-1">
              <Label>Estimated Measure Cost ($)</Label>
              <Input type="number" value={projectCost} onChange={e => setProjectCost(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Utility Incentives ($)</Label>
              <Input type="number" value={incentive} onChange={e => setIncentive(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Annual Electric Savings (kWh)</Label>
              <Input type="number" value={electricSavingsKwh} onChange={e => setElectricSavingsKwh(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Summer Peak Demand Savings (kW)</Label>
              <Input type="number" value={demandSavingsKw} onChange={e => setDemandSavingsKw(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Fossil Fuel Savings (MMBtu)</Label>
              <Input type="number" value={fossilFuelSavingsMmbtu} onChange={e => setFossilFuelSavingsMmbtu(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label>Annual Energy Cost Savings ($/yr)</Label>
              <Input type="number" value={energyCostSavings} onChange={e => setEnergyCostSavings(e.target.value)} />
            </div>
            
            <div className="space-y-1">
              <Label className="text-blue-600">Operational Savings ($/yr)</Label>
              <Input type="number" value={operationalSavings} onChange={e => setOperationalSavings(e.target.value)} />
            </div>
            
            <div className="col-span-2 space-y-2 border-t pt-4">
              <Label className="text-sm font-semibold">Attached Study Files</Label>
              
              <div className="space-y-2">
                {attachments.map((file, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-muted/30 p-2 rounded border text-xs">
                    <a 
                      href={`/api/facilities/${activeCustomerId}/download/${file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80 font-mono"
                    >
                      {file}
                    </a>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 text-destructive hover:bg-destructive/10"
                      onClick={() => setAttachments(prev => prev.filter(f => f !== file))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {attachments.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No files uploaded yet.</p>
                )}
              </div>

              <div className="pt-2">
                <Input 
                  type="file" 
                  onChange={handleFileUpload} 
                  disabled={uploading} 
                  className="text-xs"
                />
                {uploading && (
                  <div className="flex items-center text-xs text-muted-foreground mt-1">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" /> Uploading file...
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button disabled={saveMutation.isPending} onClick={handleSave}>
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Analysis & Autofill Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function InvoiceCyclesManager({ control, watch, form }: { control: any; watch: any; form: any }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "invoiceCycles"
  });

  const budgetTasks = watch("budgetTasks") || [];
  const cycles = watch("invoiceCycles") || [];

  const addCycle = () => {
    const nextNum = cycles.length + 1;
    const dateStr = nextNum === 3 ? "5-12" : `Cycle ${nextNum}`;
    
    const hours = budgetTasks.map((t: any) => ({
      taskNumber: t.taskNumber,
      onitHours: 0,
      rotbHours: 0
    }));

    append({
      date: dateStr,
      hours
    });
  };

  return (
    <Card className="border shadow-none bg-muted/5 mt-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <div>
          <CardTitle className="text-base font-semibold">Previous Billing Cycles (Excel Sheets)</CardTitle>
          <CardDescription className="text-xs">Configure previous invoice cycles to generate linked sheets and foldable summary columns.</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addCycle}>
          <Plus className="h-4 w-4 mr-2" /> Add Billing Cycle
        </Button>
      </CardHeader>
      
      <CardContent>
        {cycles.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground bg-background rounded border border-dashed">
            No previous billing cycles defined. Click "Add Billing Cycle" to configure.
          </div>
        ) : (
          <Tabs defaultValue={cycles[0]?.date} className="w-full">
            <div className="flex items-center justify-between border-b pb-2 mb-4">
              <TabsList className="flex-wrap h-auto gap-1 bg-muted/50 p-1">
                {cycles.map((cycle: any, idx: number) => (
                  <TabsTrigger key={idx} value={cycle.date} className="text-xs py-1 px-2.5">
                    {cycle.date}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {fields.map((field, cycleIndex) => {
              const cycle = cycles[cycleIndex];
              if (!cycle) return null;

              return (
                <TabsContent key={field.id} value={cycle.date} className="space-y-4 outline-none">
                  <div className="flex gap-4 items-center mb-2">
                    <FormItem className="flex-1">
                      <FormLabel className="text-xs">Cycle Date / Sheet Name suffix</FormLabel>
                      <Input
                        value={cycle.date}
                        className="h-8 text-xs max-w-[200px]"
                        onChange={(e) => form.setValue(`invoiceCycles.${cycleIndex}.date`, e.target.value)}
                        placeholder="e.g. 5-12"
                      />
                    </FormItem>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => remove(cycleIndex)} 
                      className="text-destructive self-end h-8 hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete Cycle
                    </Button>
                  </div>

                  <div className="border rounded-md bg-background overflow-hidden">
                    <div className="grid grid-cols-[5rem_1fr_8rem_8rem] gap-2 items-center p-2 text-xs font-semibold uppercase text-muted-foreground bg-muted/30 border-b">
                      <div>Task #</div>
                      <div>Description</div>
                      <div className="text-right">ONIT Hours</div>
                      <div className="text-right">ROTB Hours</div>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto divide-y divide-muted p-1">
                      {budgetTasks.map((task: any, taskIndex: number) => {
                        const hourEntry = cycle.hours?.find((h: any) => h.taskNumber === task.taskNumber) || { onitHours: 0, rotbHours: 0 };
                        
                        return (
                          <div key={taskIndex} className="grid grid-cols-[5rem_1fr_8rem_8rem] gap-2 items-center py-1.5 px-1">
                            <div className="text-xs font-medium text-muted-foreground">{task.taskNumber}</div>
                            <div className="text-xs truncate" title={task.description}>{task.description}</div>
                            
                            <div className="text-right">
                              <Input
                                type="number"
                                value={hourEntry.onitHours}
                                className="h-7 text-xs text-right w-20 ml-auto"
                                onChange={(e) => {
                                  const hoursVal = Number(e.target.value) || 0;
                                  const updatedHours = [...(cycle.hours || [])];
                                  const idx = updatedHours.findIndex((h: any) => h.taskNumber === task.taskNumber);
                                  if (idx !== -1) {
                                    updatedHours[idx].onitHours = hoursVal;
                                  } else {
                                    updatedHours.push({ taskNumber: task.taskNumber, onitHours: hoursVal, rotbHours: 0 });
                                  }
                                  form.setValue(`invoiceCycles.${cycleIndex}.hours`, updatedHours);
                                }}
                              />
                            </div>

                            <div className="text-right">
                              <Input
                                type="number"
                                value={hourEntry.rotbHours}
                                className="h-7 text-xs text-right w-20 ml-auto"
                                onChange={(e) => {
                                  const hoursVal = Number(e.target.value) || 0;
                                  const updatedHours = [...(cycle.hours || [])];
                                  const idx = updatedHours.findIndex((h: any) => h.taskNumber === task.taskNumber);
                                  if (idx !== -1) {
                                    updatedHours[idx].rotbHours = hoursVal;
                                  } else {
                                    updatedHours.push({ taskNumber: task.taskNumber, onitHours: 0, rotbHours: hoursVal });
                                  }
                                  form.setValue(`invoiceCycles.${cycleIndex}.hours`, updatedHours);
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

const fosterChildBudgetTasks = [
  { taskNumber: "1a", description: "Develop energy management plan", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 40, multiplier: 1 },
  { taskNumber: "1b", description: "Develop energy management plan", roleTitle: "Company Staff / Contractors", rate: 80, hours: 20, multiplier: 1 },
  { taskNumber: "2a", description: "Develop energy tracking & monitoring system", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 100, multiplier: 1 },
  { taskNumber: "2b", description: "Develop energy tracking & monitoring system", roleTitle: "Company Staff / Contractors", rate: 80, hours: 30, multiplier: 1 },
  { taskNumber: "3a", description: "Develop a cross functional energy team and hold monthly meetings", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 24, multiplier: 1 },
  { taskNumber: "3b", description: "Develop a cross functional energy team and hold monthly meetings", roleTitle: "Company Staff / Contractors", rate: 80, hours: 25, multiplier: 1 },
  { taskNumber: "4a", description: "Develop a strategy for energy savings M&V ", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 25, multiplier: 1 },
  { taskNumber: "4b", description: "Develop a strategy for energy savings M&V ", roleTitle: "Company Staff / Contractors", rate: 80, hours: 15, multiplier: 1 },
  { taskNumber: "5", description: "Conduct walkthrough audits and reports", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 40, multiplier: 1 },
  { taskNumber: "6", description: "Review Operations and Maintenance schedules and procedures - Refine O&M procedures based on findings", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 40, multiplier: 1 },
  { taskNumber: "7", description: "Schedule 4 employee training sessions to address energy awareness and operational issues as well as how to continuously use the SEAP Platform and task orchestration", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 20, multiplier: 1 },
  { taskNumber: "8", description: "Scope, support, and financially analyze energy and productivity projects, Preliminarily earmarked for identified Projects", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 100, multiplier: 1 },
  { taskNumber: "9", description: "Develop implementation plan for selected Monitoring of each viable project. ", roleTitle: "Company Staff / Contractors", rate: 80, hours: 260, multiplier: 1 },
  { taskNumber: "10", description: "On-site meetings (kick-off and exit)", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 23, multiplier: 1 },
  { taskNumber: "11", description: "Reporting: Monthly", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 24, multiplier: 1 },
  { taskNumber: "12", description: "Reporting: Quarterly and Final Reports", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 10, multiplier: 1 },
  { taskNumber: "13", description: "M&V Tool Development: Case studies", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 30, multiplier: 1 },
  { taskNumber: "14", description: "Tool Development: Road map", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 20, multiplier: 1 },
  { taskNumber: "15", description: "Peer to Peer Sharing Approach", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 10, multiplier: 1 },
  { taskNumber: "16", description: "Peer to Peer Sharing", roleTitle: "Company Staff / Contractors", rate: 80, hours: 10, multiplier: 1 },
  { taskNumber: "17a", description: "Develop a glidepath to permanent OsEM position or continuation of role", roleTitle: "ONIT Senior Engineer", rate: 200, hours: 10, multiplier: 1 },
  { taskNumber: "17b", description: "Develop a glidepath to permanent OsEM position or continuation of role", roleTitle: "Company Staff / Contractors", rate: 80, hours: 10, multiplier: 1 },
];

const defaultEcmFindings = [
  { ecmNumber: "1.0", description: "Lighting Control", location: "some local", quarter: "Q1", installed: "N", electricSavings: 57540, demandSavings: 4.06, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 12658, projectCost: 80583, incentiveProgram: "", notes: "" },
  { ecmNumber: "2.0", description: "Plug Load Computers", location: "-", quarter: "Q1", installed: "Y", electricSavings: 8070, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 1775, projectCost: 200, incentiveProgram: "", notes: "" },
  { ecmNumber: "3.0", description: "Plug Load Fly Traps", location: "-", quarter: "Q1", installed: "Y", electricSavings: 3912, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 862, projectCost: 1000, incentiveProgram: "", notes: "" },
  { ecmNumber: "4.0", description: "Plug load Espresso Machines", location: "-", quarter: "Q1", installed: "Y", electricSavings: 30000, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 6590, projectCost: 200, incentiveProgram: "", notes: "" },
  { ecmNumber: "5.0", description: "Plug Load FETCO Coffee Machines", location: "-", quarter: "Q1", installed: "Y", electricSavings: 1728, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 380, projectCost: 100, incentiveProgram: "", notes: "" },
  { ecmNumber: "6.0", description: "Refrigeration Circ Water Pump", location: "Garage", quarter: "Q1", installed: "N", electricSavings: 12963, demandSavings: 1.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 2852, projectCost: 5613, incentiveProgram: "Con Edison", notes: "" },
  { ecmNumber: "7.0", description: "Garage Heating and Pump", location: "Garage & Roof", quarter: "Q2", installed: "N", electricSavings: 20825, demandSavings: 0.0, fossilFuelSavings: 960, fossilFuelSource: "Natural Gas", costSavings: 19270, projectCost: 19530, incentiveProgram: "Con Edison", notes: "" },
  { ecmNumber: "8.0", description: "PCP Heating and Pump", location: "North Boiler Rm", quarter: "Q3", installed: "N", electricSavings: 17805, demandSavings: 0.0, fossilFuelSavings: 405, fossilFuelSource: "Natural Gas", costSavings: 20460, projectCost: 17055, incentiveProgram: "Con Edison", notes: "" },
  { ecmNumber: "9.0", description: "Main Kitchen Exhaust VFD", location: "Main Kitchen", quarter: "Q4", installed: "N", electricSavings: 15106, demandSavings: 0.0, fossilFuelSavings: 319, fossilFuelSource: "Natural Gas", costSavings: 10500, projectCost: 7316, incentiveProgram: "Con Edison", notes: "" },
  { ecmNumber: "10.0", description: "Grill and Fryer Exhaust Fans", location: "Main Kitchen", quarter: "Q4", installed: "N", electricSavings: 9064, demandSavings: 0.0, fossilFuelSavings: 191.6, fossilFuelSource: "Natural Gas", costSavings: 4664, projectCost: 9130, incentiveProgram: "Con Edison", notes: "" },
  { ecmNumber: "11.0", description: "Laundry Exhaust VFD", location: "Laundry Center", quarter: "Q4", installed: "N", electricSavings: 9422, demandSavings: 0.0, fossilFuelSavings: 0, fossilFuelSource: "", costSavings: 2072, projectCost: 8125, incentiveProgram: "Con Edison", notes: "" },
  { ecmNumber: "12.0", description: "SEAP EMIS-ASO / BMS", location: "Roof", quarter: "Q4", installed: "N", electricSavings: 390000, demandSavings: 1.0, fossilFuelSavings: 29400, fossilFuelSource: "Natural Gas", costSavings: 143000, projectCost: 70000, incentiveProgram: "Con Edison", notes: "" }
];


export default function OsemOnboardingPage({ params }: { params: { id: string } }) {
  const isNew = params.id === "new";
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [generatedProposal, setGeneratedProposal] = useState<string | null>(null);
  const [proposalId, setProposalId] = useState<number | null>(isNew ? null : parseInt(params.id));
  const [feedback, setFeedback] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const { activeCustomerId, setActiveCustomerId } = useCustomer();

  // Fetch existing proposal
  const { data: existingProposal, isLoading: isLoadingProposal } = useQuery<any>({
    queryKey: [`/api/osem/${params.id}`],
    enabled: !isNew,
  });

  // Fetch versions
  const { data: versions } = useQuery<any[]>({
    queryKey: [`/api/osem/${params.id}/versions`],
    enabled: !isNew,
  });



  const form = useForm<OsemFormValues>({
    resolver: zodResolver(osemFormSchema),
    defaultValues: {
      companyName: "",
      companyDescription: "",
      sustainabilityGoals: "",
      pastProjectsHistory: "",
      weeklyHours: 20,
      onSitePercentage: 50,
      businessType: "",
      equipmentOverview: "",
      softwarePlatforms: "ONIT SEAP Platform",
      staffCulture: "Highly dedicated and driven on customer service and safety.",
      trainingRequirements: "Four multi-hour training sessions on workflow and procedures.",
      facilities: [{ name: "", address: "", equipmentProfile: "" }],
      organizationSetup: "",
      energyManagerAgency: "ONIT Global, Inc. (www.onit.global)",
      glidepathPlan: "It’s our plan to leverage the OsEM effort and embed SEAP tools into our operations.",
      electricUsageKwh: 0,
      electricCost: 0,
      gasUsageTherms: 0,
      gasCost: 0,
      electricSavingsTarget: 10,
      fossilFuelSavingsTarget: 10,
      executiveSponsorName: "",
      executiveSponsorTitle: "",
      orgChart: [],
      onitExpert: "John A. Johnson, PE",
      potentialProjects: [],
      targetEnergySavings: "10%",
      seapPlatformTraining: true,
      developTrainingMaterial: true,
      omProceduresSupport: true,
      trainingVideosCreation: "modest number",
      additionalTasks: [],
      taskDeliverables: {
        task1: "Team Charter",
        task2: "Draft EMP and Final EMP",
        task3: "Configured energy dashboard",
        task4: "Audit Report with 20+ ECMs",
        task5: "Baseline & M&V Report",
        task6: "Feasibility reports",
        task7: "Staff certifications",
        task8: "Implementation verification report",
        task9: "Implementation verification report",
        task10: "Handover protocol documentation",
        task11: "Case study and presentation slides"
      },
      projectSchedule: [],
      durationMonths: 12,
      budgetTasks: fosterChildBudgetTasks,
      ecmFindings: [],
      invoiceCycles: [
        {
          date: "2-14",
          hours: [
            { taskNumber: "1a", onitHours: 2, rotbHours: 0 },
            { taskNumber: "2a", onitHours: 3, rotbHours: 0 },
            { taskNumber: "3a", onitHours: 2, rotbHours: 0 },
            { taskNumber: "5", onitHours: 35, rotbHours: 0 },
            { taskNumber: "8", onitHours: 50, rotbHours: 0 },
            { taskNumber: "9", onitHours: 0, rotbHours: 56.9 },
            { taskNumber: "10", onitHours: 3, rotbHours: 0 }
          ]
        },
        {
          date: "4-8",
          hours: [
            { taskNumber: "1a", onitHours: 16, rotbHours: 20 },
            { taskNumber: "2a", onitHours: 21, rotbHours: 0 },
            { taskNumber: "3a", onitHours: 1, rotbHours: 0 },
            { taskNumber: "5", onitHours: 16, rotbHours: 0 },
            { taskNumber: "6", onitHours: 5, rotbHours: 0 },
            { taskNumber: "7", onitHours: 2, rotbHours: 0 },
            { taskNumber: "8", onitHours: 7, rotbHours: 0 },
            { taskNumber: "9", onitHours: 0, rotbHours: 40 },
            { taskNumber: "11", onitHours: 4, rotbHours: 0 }
          ]
        }
      ],
      hourlyRate: 150,
      nyserdaCostSharePercentage: 75,
      travelBudget: 0,
      materialsBudget: 0,
    },
  });

  useEffect(() => {
    // Only reset form if we haven't selected a specific past version
    if (existingProposal && !selectedVersionId) {
      form.reset(existingProposal);
      if (existingProposal.generatedProposal) {
        setGeneratedProposal(existingProposal.generatedProposal);
      }
      if (existingProposal.buildingId && activeCustomerId !== existingProposal.buildingId) {
        setActiveCustomerId(existingProposal.buildingId);
      }
    }
  }, [existingProposal, form, selectedVersionId, activeCustomerId, setActiveCustomerId]);

  const handleVersionSelect = (versionIdStr: string) => {
    if (versionIdStr === "current") {
      setSelectedVersionId(null);
      if (existingProposal) {
        form.reset(existingProposal);
        setGeneratedProposal(existingProposal.generatedProposal || null);
      }
    } else {
      const vId = parseInt(versionIdStr);
      setSelectedVersionId(vId);
      const selectedVersion = versions?.find(v => v.id === vId);
      if (selectedVersion) {
        form.reset(selectedVersion.formData);
        setGeneratedProposal(selectedVersion.generatedProposal);
      }
    }
  };

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "facilities",
  });

  const { fields: orgFields, append: appendOrg, remove: removeOrg } = useFieldArray({
    control: form.control,
    name: "orgChart",
  });

  const { fields: taskFields, append: appendTask, remove: removeTask } = useFieldArray({
    control: form.control,
    name: "additionalTasks",
  });

  const { fields: scheduleCategories, append: appendScheduleCategory, remove: removeScheduleCategory } = useFieldArray({
    control: form.control,
    name: "projectSchedule",
  });



  const createProposalMutation = useMutation({
    mutationFn: async (data: OsemFormValues) => {
      const payload = activeCustomerId ? { ...data, customerId: activeCustomerId } : data;
      const res = await apiRequest("POST", "/api/osem", payload);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0].startsWith("/api/osem") || query.queryKey[0].startsWith("/api/admin/osem"))
      });
    }
  });

  const updateProposalMutation = useMutation({
    mutationFn: async (payload: { id: number; data: OsemFormValues; newVersion?: boolean }) => {
      const url = `/api/osem/${payload.id}${payload.newVersion ? "?newVersion=true" : ""}`;
      const res = await apiRequest("PUT", url, payload.data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0].startsWith("/api/osem") || query.queryKey[0].startsWith("/api/admin/osem"))
      });
    }
  });
  const approveProposalMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/osem/${id}/approve`, {});
      return await res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0].startsWith("/api/osem") || query.queryKey[0].startsWith("/api/admin/osem"))
      });
      toast({
        title: "Proposal Approved",
        description: "The proposal has been approved and promoted to the active Energy Management Plan."
      });
      setLocation(`/osem-onboarding/${proposalId}/emp`);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to approve proposal: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  const generateLLMMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/osem/${id}/generate`, {});
      return await res.json();
    },
    onSuccess: (data) => {
      setGeneratedProposal(data.generatedProposal);
      
      // Auto-populate the Schedule Builder UI
      if (data.projectSchedule && Array.isArray(data.projectSchedule) && data.projectSchedule.length > 0) {
        form.setValue("projectSchedule", data.projectSchedule);
      }
      
      // Auto-populate the Budget Builder UI
      if (data.budgetTasks && Array.isArray(data.budgetTasks) && data.budgetTasks.length > 0) {
        form.setValue("budgetTasks", data.budgetTasks);
      }

      // Auto-populate the ECM Findings UI
      if (data.ecmFindings && Array.isArray(data.ecmFindings) && data.ecmFindings.length > 0) {
        form.setValue("ecmFindings", data.ecmFindings);
      }

      toast({
        title: "Proposal Generated",
        description: "Review the proposed NYSERDA OsEM concept draft below.",
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0].startsWith("/api/osem") || query.queryKey[0].startsWith("/api/admin/osem"))
      });
    },
    onError: (error) => {
      toast({
        title: "Generation Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const reviseLLMMutation = useMutation({
    mutationFn: async (payload: { id: number, feedback: string }) => {
      const res = await apiRequest("POST", `/api/osem/${payload.id}/revise`, { feedback: payload.feedback });
      return await res.json();
    },
    onSuccess: (data) => {
      setGeneratedProposal(data.generatedProposal);
      // Automatically update the form to reflect Grok's changes
      form.reset({
        companyName: data.companyName,
        companyDescription: data.companyDescription || "",
        sustainabilityGoals: data.sustainabilityGoals || "",
        pastProjectsHistory: data.pastProjectsHistory || "",
        weeklyHours: data.weeklyHours || 20,
        onSitePercentage: data.onSitePercentage || 50,
        businessType: data.businessType || "",
        equipmentOverview: data.equipmentOverview || "",
        softwarePlatforms: data.softwarePlatforms || "ONIT SEAP Platform",
        staffCulture: data.staffCulture || "",
        trainingRequirements: data.trainingRequirements || "",
        facilities: data.facilities,
        organizationSetup: data.organizationSetup || "",
        energyManagerAgency: data.energyManagerAgency || "ONIT Global, Inc.",
        glidepathPlan: data.glidepathPlan || "",
        electricUsageKwh: data.electricUsageKwh || 0,
        electricCost: data.electricCost || 0,
        gasUsageTherms: data.gasUsageTherms || 0,
        gasCost: data.gasCost || 0,
        electricSavingsTarget: data.electricSavingsTarget || 10,
        fossilFuelSavingsTarget: data.fossilFuelSavingsTarget || 10,
        executiveSponsorName: data.executiveSponsorName || "",
        executiveSponsorTitle: data.executiveSponsorTitle || "",
        orgChart: data.orgChart || [],
        onitExpert: data.onitExpert || "John A. Johnson, PE",
        potentialProjects: data.potentialProjects || [],
        targetEnergySavings: data.targetEnergySavings || "10%",
        seapPlatformTraining: data.seapPlatformTraining !== undefined ? data.seapPlatformTraining : true,
        developTrainingMaterial: data.developTrainingMaterial !== undefined ? data.developTrainingMaterial : true,
        omProceduresSupport: data.omProceduresSupport !== undefined ? data.omProceduresSupport : true,
        trainingVideosCreation: data.trainingVideosCreation || "modest number",
        additionalTasks: data.additionalTasks || [],
        taskDeliverables: data.taskDeliverables || {},
        hourlyRate: data.hourlyRate || 150,
        nyserdaCostSharePercentage: data.nyserdaCostSharePercentage || 75,
        travelBudget: data.travelBudget || 0,
        materialsBudget: data.materialsBudget || 0,
        durationMonths: data.durationMonths || 12,
        projectSchedule: data.projectSchedule || [],
        budgetTasks: data.budgetTasks || [],
        ecmFindings: data.ecmFindings || [],
        invoiceCycles: data.invoiceCycles || [],
      });
      setFeedback("");
      toast({
        title: "Proposal Revised",
        description: "Grok has updated the proposal and form entries.",
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0].startsWith("/api/osem") || query.queryKey[0].startsWith("/api/admin/osem"))
      });
    },
    onError: (error) => {
      toast({
        title: "Revision Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  if (!isNew && isLoadingProposal) {
    return (
      <AppShell>
        <div className="flex justify-center items-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  const onSaveDraft = async (data: OsemFormValues, newVersion: boolean = false) => {
    try {
      if (isNew) {
        const createdObj = await createProposalMutation.mutateAsync(data);
        setProposalId(createdObj.id);
        toast({
          title: "Draft Saved",
          description: "Your proposal details have been saved.",
        });
        setLocation(`/osem-onboarding/${createdObj.id}`, { replace: true });
      } else {
        await updateProposalMutation.mutateAsync({ id: proposalId!, data, newVersion });
        toast({
          title: newVersion ? "Version Snapshot Created" : "Draft Updated",
          description: newVersion 
            ? "A new version snapshot was successfully created with current timestamp." 
            : "Your changes have been saved.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to save the draft.",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: OsemFormValues) => {
    let createdId = proposalId;
    try {
      if (isNew) {
        const createdObj = await createProposalMutation.mutateAsync(data);
        createdId = createdObj.id;
        setProposalId(createdObj.id);
        toast({
          title: "Form Saved",
          description: "Processing requirements via AI to generate proposal draft...",
        });
        // We navigate to replace '/new' with the new ID so refreshes work
        setLocation(`/osem-onboarding/${createdObj.id}`, { replace: true });
      } else {
        await updateProposalMutation.mutateAsync({ id: proposalId!, data });
        toast({
          title: "Form Updated",
          description: "Changes saved. Processing AI generation...",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to save the onboarding form.",
        variant: "destructive",
      });
      return;
    }

    // Automatically generate proposal
    try {
      await generateLLMMutation.mutateAsync(createdId as number);
    } catch (error: any) {
      // The generateLLMMutation.onError handler will handle the toast notification
      console.error("AI Generation failed:", error);
    }
  };
  const downloadMarkdown = () => {
    if (!generatedProposal) return;
    const blob = new Blob([generatedProposal], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${form.watch("companyName")?.replace(/\s+/g, '_') || 'OsEM'}_Proposal.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadDoc = () => {
    if (!generatedProposal) return;

    // Convert basic Markdown to structured Word-compatible HTML
    const lines = generatedProposal.split('\n');
    let inTable = false;
    let tableRows: string[] = [];
    const htmlLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('|') && line.endsWith('|')) {
        if (line.match(/^\|[\s\-\:|]+\|$/)) {
          // Skip markdown table delimiter row
          continue;
        }
        inTable = true;
        const cells = line.substring(1, line.length - 1).split('|').map(c => c.trim());
        const isHeader = tableRows.length === 0;
        const cellTag = isHeader ? 'th' : 'td';
        const rowHtml = `<tr style="${isHeader ? 'background-color: #f2f2f2; font-weight: bold;' : ''}">${cells.map(c => `<${cellTag} style="border: 1px solid #dddddd; padding: 8px; text-align: left;">${c}</${cellTag}>`).join('')}</tr>`;
        tableRows.push(rowHtml);
      } else {
        if (inTable) {
          htmlLines.push(`<table style="font-family: Arial, sans-serif; border-collapse: collapse; width: 100%; margin: 15px 0;">${tableRows.join('')}</table>`);
          tableRows = [];
          inTable = false;
        }

        if (line.startsWith('# ')) {
          htmlLines.push(`<h1 style="font-family: Arial, sans-serif; color: #1a365d; margin-top: 20px; margin-bottom: 10px; font-size: 18pt;">${line.substring(2)}</h1>`);
        } else if (line.startsWith('## ')) {
          htmlLines.push(`<h2 style="font-family: Arial, sans-serif; color: #2b6cb0; margin-top: 15px; margin-bottom: 8px; font-size: 14pt; border-bottom: 1px solid #e2e8f0; padding-bottom: 3px;">${line.substring(3)}</h2>`);
        } else if (line.startsWith('### ')) {
          htmlLines.push(`<h3 style="font-family: Arial, sans-serif; color: #4a5568; margin-top: 12px; margin-bottom: 6px; font-size: 12pt;">${line.substring(4)}</h3>`);
        } else if (line.startsWith('- ')) {
          htmlLines.push(`<p style="font-family: Arial, sans-serif; margin-left: 20px; margin-top: 3px; margin-bottom: 3px; font-size: 11pt;">• ${line.substring(2)}</p>`);
        } else if (line.startsWith('* ')) {
          htmlLines.push(`<p style="font-family: Arial, sans-serif; margin-left: 20px; margin-top: 3px; margin-bottom: 3px; font-size: 11pt;">• ${line.substring(2)}</p>`);
        } else if (line === '') {
          // Empty line
        } else {
          htmlLines.push(`<p style="font-family: Arial, sans-serif; line-height: 1.5; margin-top: 6px; margin-bottom: 6px; font-size: 11pt;">${line}</p>`);
        }
      }
    }
    if (inTable) {
      htmlLines.push(`<table style="font-family: Arial, sans-serif; border-collapse: collapse; width: 100%; margin: 15px 0;">${tableRows.join('')}</table>`);
    }

    let parsedBody = htmlLines.join('\n');
    // Replace markdown bold (**text**) with HTML strong
    parsedBody = parsedBody.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <meta charset='utf-8'>
      </head>
      <body style="padding: 20px;">
        ${parsedBody}
      </body>
      </html>
    `;
    const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${form.watch("companyName")?.replace(/\s+/g, '_') || 'OsEM'}_Proposal.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadExcel = () => {
    if (!generatedProposal) return;

    const tables: string[][][] = [];
    const lines = generatedProposal.split('\n');
    let currentTable: string[][] | null = null;

    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        if (!currentTable) {
          currentTable = [];
          tables.push(currentTable);
        }
        if (trimmed.match(/^\|[\s\-\:|]+\|$/)) continue; // skip markdown separator row
        
        const row = trimmed.substring(1, trimmed.length - 1).split('|').map(cell => cell.trim());
        currentTable.push(row);
      } else {
        currentTable = null;
      }
    }

    if (tables.length === 0) {
      toast({
        title: "No tables found",
        description: "The generated proposal does not contain any tables to convert into spreadsheets.",
        variant: "destructive",
      });
      return;
    }

    const wb = XLSX.utils.book_new();
    tables.forEach((tableData, idx) => {
      const ws = XLSX.utils.aoa_to_sheet(tableData);
      XLSX.utils.book_append_sheet(wb, ws, `Attachment ${String.fromCharCode(65 + idx)}`);
    });

    XLSX.writeFile(wb, `${form.watch("companyName")?.replace(/\s+/g, '_') || 'OsEM'}_Attachments.xlsx`);
  };

  const downloadAttachmentA = () => {
    const schedule = form.watch("projectSchedule");
    const durationMonths = form.watch("durationMonths") || 12;
    if (!schedule || schedule.length === 0) {
      toast({
        title: "No Schedule Data",
        description: "Please define the project schedule in the form before downloading Attachment A.",
        variant: "destructive"
      });
      return;
    }

    const wsData: any[][] = [];
    wsData.push([]); // Row 1: Empty
    wsData.push(["", `Attachment A - OsEM Schedule - ${form.watch("companyName") || "Company"}`]); // Row 2: Title
    
    // Row 3: Headers
    const headers = ["", ...Array.from({ length: durationMonths }).map((_, i) => `M${i + 1}`)];
    wsData.push(headers);
    
    // Row 4: Subtitle
    wsData.push(["Deliverables to NYSERDA and Company Objectives with OsEM"]);
    
    schedule.forEach(cat => {
      wsData.push([cat.category]); // Category title
      cat.tasks.forEach(task => {
        const row = [task.description];
        for (let i = 1; i <= durationMonths; i++) {
          row.push((task.months && task.months.includes(i)) ? "" : "");
        }
        wsData.push(row);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attachment A");
    XLSX.writeFile(wb, `${form.watch("companyName")?.replace(/\s+/g, '_') || 'OsEM'}_Attachment_A_Schedule.xlsx`);
  };

  const handleUploadSchedule = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0]; // Assuming first sheet
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });

        const durationMonths = form.watch("durationMonths") || 12;
        const newSchedule: { category: string, tasks: { description: string, months: number[] }[] }[] = [];
        let currentCategory = "";
        let currentTasks: { description: string, months: number[] }[] = [];

        // Data expected to start roughly at index 4 (row 5)
        for (let r = 4; r < data.length; r++) {
          const row = data[r];
          if (!row || row.length === 0) continue;
          
          if (row[0] && row.length === 1) {
            // It's a category
            if (currentCategory && currentTasks.length > 0) {
              newSchedule.push({ category: currentCategory, tasks: currentTasks });
            }
            currentCategory = row[0];
            currentTasks = [];
          } else if (row[0] && row.length > 1) {
            // It's a task.
            const months: number[] = [];
            for (let i = 1; i <= durationMonths; i++) {
              if (row[i] === "" || row[i] === "x" || row[i] === "X" || row[i] === "✓") {
                months.push(i);
              }
            }
            currentTasks.push({ description: row[0], months });
          }
        }
        if (currentCategory && currentTasks.length > 0) {
           newSchedule.push({ category: currentCategory, tasks: currentTasks });
        }

        if (newSchedule.length > 0) {
          form.setValue("projectSchedule", newSchedule);
          toast({ title: "Success", description: "Schedule imported from Excel!" });
        } else {
          toast({ title: "Empty", description: "No schedule data found.", variant: "destructive" });
        }
      } catch (err) {
        toast({ title: "Error", description: "Failed to parse Schedule file.", variant: "destructive" });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const getColLetter = (idx: number): string => {
    let temp = idx;
    let letter = "";
    while (temp >= 0) {
      letter = String.fromCharCode((temp % 26) + 65) + letter;
      temp = Math.floor(temp / 26) - 1;
    }
    return letter;
  };

  const generateCycleSheet = (cycle: any, companyName: string) => {
    const wsData: any[][] = [];
    
    // Header row for ONIT invoices
    wsData.push([
      "ONIT INVOICES PAID THIS BILLING CYCLE", 
      "", 
      "Invoice", 
      "",
      "hour code 1a", "hour code 2a", "hour code 3a", "hour code 4a", 
      "hour code 5", "hour code 6", "hour code 7", "hour code 8", 
      " ", 
      "hour code 10", "hour code 11", "hour code 12", "hour code 13", 
      "hour code 14", "hour code 15", 
      " ", 
      "hour code 17a"
    ]);

    // We place the ONIT hours in Row 2 (index 1)
    const row2: any[] = ["", "", "#001", ""];
    const onitTasks = ["1a", "2a", "3a", "4a", "5", "6", "7", "8", "spacer", "10", "11", "12", "13", "14", "15", "spacer2", "17a"];
    onitTasks.forEach((task) => {
      if (task.startsWith("spacer")) {
        row2.push(" ");
      } else {
        const match = cycle.hours?.find((h: any) => h.taskNumber === task);
        row2.push(match ? match.onitHours : "");
      }
    });
    wsData.push(row2);

    // Fill rows 3-6 with empty rows matching the original layout
    for (let i = 2; i <= 5; i++) {
      wsData.push(["", "", `#00${i}`, ""]);
    }

    // Row 7 (index 6): headers
    wsData.push([
      "", "", "", "", 
      "hour code 1a", "hour code 2a", "hour code 3", "hour code 4", 
      "hour code 5", "hour code 6", "hour code 7", "hour code 8", 
      " ", 
      "hour code 10", "hour code 11", "hour code 12", "hour code 13", 
      "hour code 14", "hour code 15", 
      " ", 
      "hour code 17a"
    ]);

    // Row 8 (index 7): Total row
    wsData.push([
      "", "", "Total Cycle Invoice", "",
      { f: "SUM(E2:E6)" }, { f: "SUM(F2:F6)" }, { f: "SUM(G2:G6)" }, { f: "SUM(H2:H6)" },
      { f: "SUM(I2:I6)" }, { f: "SUM(J2:J6)" }, { f: "SUM(K2:K6)" }, { f: "SUM(L2:L6)" },
      " ",
      { f: "SUM(N2:N6)" }, { f: "SUM(O2:O6)" }, { f: "SUM(P2:P6)" }, { f: "SUM(Q2:Q6)" },
      { f: "SUM(R2:R6)" }, { f: "SUM(S2:S6)" },
      " ",
      { f: "SUM(U2:U6)" },
      { f: "SUM(E8:U8)" }
    ]);

    wsData.push(["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "$200 per hour"]);
    
    // Row 10 (index 9)
    wsData.push([
      `${companyName}'s Hour paper`, 
      "ROTB Hours", 
      "NYSERDA Equivalent Hours", 
      "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "",
      { f: "V8*200" }
    ]);

    // Rows 11-18 (indices 10-17) - NYSERDA equivalent hours breakdown
    wsData.push([571.2, " ", { f: "A11/80" }]);
    for (let i = 12; i <= 18; i++) {
      wsData.push(["", "", { f: `A${i}/80` }]);
    }

    // Row 19 (index 18)
    wsData.push([
      "Total Hours per NYSERDA Hours", 
      "", 
      { f: "SUM(C11:C18)" }, 
      "ROTB Invoiced Hours",
      "hour code 1b", "hour code 2b", "hour code 3b", "hour code 4b", 
      " ", " ", " ", " ", 
      "hour code 9", 
      "", "", "", "", "", "", 
      "hour code 16", "hour code 17b", 
      "Total Hours"
    ]);

    // Row 20 (index 19): ROTB hours values
    const row20: any[] = ["", "", "", "ROTB Invoiced Hours"];
    const rotbTasks = ["1b", "2b", "3b", "4b", "spacer", "spacer2", "spacer3", "spacer4", "9", "spacer5", "spacer6", "spacer7", "spacer8", "spacer9", "spacer10", "spacer11", "16", "17b"];
    rotbTasks.forEach((task) => {
      if (task.startsWith("spacer")) {
        row20.push("");
      } else {
        const match = cycle.hours?.find((h: any) => h.taskNumber === task);
        row20.push(match ? match.rotbHours : "");
      }
    });
    row20.push({ f: "SUM(E20:U20)" });
    wsData.push(row20);

    // Row 21-22
    wsData.push(["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "$80 per hour"]);
    wsData.push(["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", { f: "V20*80" }]);
    
    // Spacer rows
    wsData.push([]);
    wsData.push([]);

    // Row 25 & 26 (index 24 & 25)
    wsData.push(["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "Total Billed", { f: "V10+V22" }]);
    wsData.push(["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", 0.25, { f: "V25*0.25" }]);

    return wsData;
  };

  const downloadBudgetExcel = async () => {
    const budgetTasks = form.watch("budgetTasks");
    if (!budgetTasks || budgetTasks.length === 0) {
      toast({
        title: "No Budget Data",
        description: "Please define the budget tasks in the form before downloading the Budget.",
        variant: "destructive"
      });
      return;
    }

    if (!proposalId) {
      toast({ title: "Save First", description: "Please save the proposal before downloading.", variant: "destructive" });
      return;
    }

    try {
      const companyName = form.watch("companyName") || "Company Name";
      const invoiceCycles = form.watch("invoiceCycles") || [];
      const res = await fetch(resolveUrl(`/api/osem/${proposalId}/download-invoice`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ budgetTasks, invoiceCycles })
      });
      if (!res.ok) throw new Error("Failed to generate invoice workbook");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${companyName.replace(/\s+/g, '_')}_Invoice_OeSM.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: "Download Failed", description: err.message, variant: "destructive" });
    }
  };

  const downloadAppendixPExcel = async () => {
    const ecmFindings = form.watch("ecmFindings") || [];
    if (!ecmFindings || ecmFindings.length === 0) {
      toast({
        title: "No Findings Data",
        description: "Please define the ECM findings in the form before downloading Appendix P.",
        variant: "destructive"
      });
      return;
    }

    if (!proposalId) {
      toast({ title: "Save First", description: "Please save the proposal before downloading.", variant: "destructive" });
      return;
    }

    try {
      const companyName = form.watch("companyName") || "Company Name";
      const res = await fetch(resolveUrl(`/api/osem/${proposalId}/download-appendix-p`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ecmFindings })
      });
      if (!res.ok) throw new Error("Failed to generate NYSERDA workbook");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${companyName.replace(/\s+/g, '_')}_Appendix_P_NYSERDA_Project_Summary_Sheet.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: "Download Failed", description: err.message, variant: "destructive" });
    }
  };

  const handleUploadBudget = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        // The budget is strictly on "Budget" sheet or first if missing
        const wsname = wb.SheetNames.find(n => n.includes("Budget")) || wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });

        const newBudgetTasks: any[] = [];
        
        // Let's iterate data. Budget items are expected around row 15 (index 14) onwards.
        for (let r = 0; r < data.length; r++) {
          const row = data[r];
          if (!row) continue;
          // Look for valid Task Numbers in col 0.
          if (row[0] && row[1] && row[0] !== "Task" && row[0] !== "Facility:" && row[1] !== "Total" && typeof row[0] === 'string' && row[0].match(/^[0-9]+[a-z]?$/i)) {
             newBudgetTasks.push({
               taskNumber: row[0].toString(),
               description: (row[1] || "").toString(),
               roleTitle: (row[5] || "").toString(),
               rate: Number(row[9]) || 0,
               hours: Number(row[10]) || 0,
               multiplier: Number(row[12]) || 1
             });
          }
        }

        if (newBudgetTasks.length > 0) {
          form.setValue("budgetTasks", newBudgetTasks);
          toast({ title: "Success", description: "Budget tasks imported via Excel!" });
        } else {
          toast({ title: "Warning", description: "No valid tasks found in Excel Budget.", variant: "destructive" });
        }
      } catch (err) {
        toast({ title: "Error", description: "Failed to parse Budget Excel file.", variant: "destructive" });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };


  return (
    <ErrorBoundary>
      <AppShell fluid>
      <div className="w-full max-w-[98%] px-4 mx-auto space-y-8 pb-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-4 gap-4 no-print">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">OsEM Project Proposal Onboarding</h1>
            <p className="text-muted-foreground">
              Fill out the operational details to automatically synthesize a comprehensive NYSERDA On-site Energy Manager (OsEM) Application Proposal.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => window.print()}
          >
            <Printer className="h-4 w-4 mr-2" /> Print to PDF
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                
                {/* Section 1: OsEM Concept (Attachment B) */}
                <Card>
                  <CardHeader>
                    <CardTitle>1. OsEM Concept (Attachment B)</CardTitle>
                    <CardDescription>Background on the company, facilities, and the goal/vision for this OsEM engagement.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Company/Organization Name 
                            <HelpTooltip content="Legal entity name holding the facilities. E.g. Russo Enterprises, Corp." />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Russo Enterprises, Corp." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="businessType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Business Type
                            <HelpTooltip content="E.g. Hospitality & Food Manufacturing" />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="E.g. Hospitality & Food Manufacturing" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="equipmentOverview"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Equipment Overview
                            <HelpTooltip content="Provide specific context about your facilities. E.g. 1990s vintage BMS, 18 walk-in refrigerators..." />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="Describe the current equipment and system vintage..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companyDescription"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Company Background & Services 
                            <HelpTooltip content="Explain what your company does. E.g. As a hospitality facility and manufacturer of packaged meals for nursing homes and hotels." />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="Describe the background and main function of the company..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="sustainabilityGoals"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Existing Corporate Sustainability Goals
                            <HelpTooltip content="Detail any existing energy reduction goals, carbon reduction targets, or green commitments." />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="E.g. Reduce carbon footprint by 20% over 5 years, target Net Zero operations..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pastProjectsHistory"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Energy efficiency track record & past projects
                            <HelpTooltip content="Highlight past energy conservation efforts, equipment upgrades, or utility recognitions." />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="E.g. Completed LED lighting retrofits in 2024, replaced a boiler in 2022..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="softwarePlatforms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Software Platforms
                            <HelpTooltip content="E.g. ONIT SEAP Platform, Inspection & LIFE modules." />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="E.g. ONIT SEAP Platform" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="staffCulture"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Current Staff Culture
                            <HelpTooltip content="E.g. Highly dedicated and motivated, driven on customer service..." />
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Current staff culture" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="trainingRequirements"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Training Requirements
                            <HelpTooltip content="Detail the specific training. E.g. Four training sessions focusing on workflow..." />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="Staff training requirements..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-4 pt-4 border-t mt-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Additional Scope & Training Options</h4>
                      <FormField
                        control={form.control}
                        name="seapPlatformTraining"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-md">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>SEAP Platform Training</FormLabel>
                              <FormDescription>Train the group on how to use the SEAP platform.</FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="developTrainingMaterial"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-md">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Site-Specific Training Material</FormLabel>
                              <FormDescription>Support the development of training material specifically related to equipment on site via the knowledge module.</FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="omProceduresSupport"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-md">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>O&M Procedures & Workflows</FormLabel>
                              <FormDescription>Support culling existing/internet found O&M procedures of equipment and to develop O&M workflows.</FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="trainingVideosCreation"
                        render={({ field }) => (
                          <FormItem className="pt-2">
                            <FormLabel>Training Videos Scope</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. modest number of training videos..." {...field} />
                            </FormControl>
                            <FormDescription>Describe the scope of training videos on operational excellence specific to their equipment.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="organizationSetup"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center">
                            Current Organization & Energy Practices
                            <HelpTooltip content="How is energy currently managed? E.g. 'The current practice is to outsource maintenance of equipment to third-party vendors with little focus by internal staff...'" />
                          </FormLabel>
                          <FormControl>
                            <Textarea placeholder="The current energy management practice is to primarily outsource maintenance..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                       control={form.control}
                       name="glidepathPlan"
                       render={({ field }) => (
                         <FormItem>
                           <FormLabel className="flex items-center">
                             Vision & Glidepath 
                             <HelpTooltip content="What is the vision post-pilot? E.g. 'To embed ONIT’s tools and expertise into our operations people to drive footprint reduction.'" />
                           </FormLabel>
                           <FormControl>
                             <Textarea placeholder="It’s our plan to leverage the OsEM effort and embed tools into our operations..." {...field} />
                           </FormControl>
                           <FormMessage />
                         </FormItem>
                       )}
                    />
                  </CardContent>
                </Card>

                {/* Section 2: Facilities */}
                <Card>
                  <CardHeader>
                    <CardTitle>Facilities to Include</CardTitle>
                    <CardDescription>Add all facilities relevant to this OsEM program.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {fields.map((field, index) => (
                      <div key={field.id} className="p-4 border rounded-md space-y-4 relative">
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 text-destructive"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Facility {index + 1}</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name={`facilities.${index}.name` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Facility Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Main Plant" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`facilities.${index}.address` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Address</FormLabel>
                                <FormControl>
                                  <Input placeholder="123 Industrial Way, NY" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name={`facilities.${index}.equipmentProfile` as const}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center">
                                Equipment & Operation Profile
                                <HelpTooltip content="Describe the age/type of equipment. E.g. 'Constructed in the 1990s. We have 18 walk-in refrigeration units, RTUs on the roof, and ovens heated with natural gas.'" />
                              </FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Describe the main uses of energy (e.g. 1990s RTUs, walk-in freezers, boilers, cooking lines, etc.)" 
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => append({ name: "", address: "", equipmentProfile: "" })}
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Another Facility
                    </Button>
                  </CardContent>
                </Card>

                {/* Section 3: Energy Baselines & Goals */}
                <Card>
                  <CardHeader>
                    <CardTitle>3. Statement of Work (Attachment C)</CardTitle>
                    <CardDescription>Baseline energy usage and target savings to determine scope of work.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <FormField control={form.control} name="energyManagerAgency" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          Energy Manager Agency
                          <HelpTooltip content="The contracted party providing the Energy Manager platforms and expertise. E.g. 'ONIT Global, Inc. (www.onit.global)'" />
                        </FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}/>

                    <FormField control={form.control} name="targetEnergySavings" render={({ field }) => (
                      <FormItem className="pt-4 border-t"><FormLabel>Target Overall Savings</FormLabel><FormControl><Input placeholder="e.g. 10%" {...field} /></FormControl><FormDescription>General target savings goal text</FormDescription><FormMessage /></FormItem>
                    )}/>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                      <div className="space-y-4">
                        <h4 className="font-medium">Electricity</h4>
                        <FormField control={form.control} name="electricUsageKwh" render={({ field }) => (
                          <FormItem><FormLabel>Annual Usage (kWh)</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
                        )}/>
                        <FormField control={form.control} name="electricCost" render={({ field }) => (
                          <FormItem><FormLabel>Annual Cost ($)</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
                        )}/>
                        <FormField control={form.control} name="electricSavingsTarget" render={({ field }) => (
                          <FormItem><FormLabel>Savings Target (%)</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
                        )}/>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-medium">Fossil Fuels (Natural Gas)</h4>
                        <FormField control={form.control} name="gasUsageTherms" render={({ field }) => (
                          <FormItem><FormLabel>Annual Usage (Therms/CCF)</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
                        )}/>
                        <FormField control={form.control} name="gasCost" render={({ field }) => (
                          <FormItem><FormLabel>Annual Cost ($)</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
                        )}/>
                        <FormField control={form.control} name="fossilFuelSavingsTarget" render={({ field }) => (
                          <FormItem><FormLabel>Savings Target (%)</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
                        )}/>
                      </div>
                    </div>

                    <div className="pt-6 border-t mt-6">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <FormLabel className="text-base flex items-center">
                            Energy Conservation Measures (ECMs) / Findings (Appendix P)
                            <HelpTooltip content="Define the list of identified energy conservation measures (ECMs). These will generate the NYSERDA Appendix P project summary spreadsheet." />
                          </FormLabel>
                          <p className="text-xs text-muted-foreground">List the measures identified, including annual savings, costs, paybacks, and Con Edison incentives.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!proposalId}
                            onClick={() => setLocation(`/osem-onboarding/${proposalId}/lighting`)}
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2" /> Lighting Auditor
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={downloadAppendixPExcel}
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Appendix P
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto pb-4">
                        <div className="min-w-[800px]">
                          <EcmFindingsTable control={form.control} watch={form.watch} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 4: Leadership and Projects */}
                <Card>
                  <CardHeader>
                    <CardTitle>2. Organization & Delivery Structure</CardTitle>
                    <CardDescription>Identify the cross-functional energy team and OsEM task priorities.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField control={form.control} name="executiveSponsorName" render={({ field }) => (
                        <FormItem><FormLabel>Executive Sponsor Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )}/>
                      <FormField control={form.control} name="executiveSponsorTitle" render={({ field }) => (
                        <FormItem><FormLabel>Sponsor Title</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )}/>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField control={form.control} name="onitExpert" render={({ field }) => (
                         <FormItem>
                           <FormLabel className="flex items-center">
                             ONIT / Outside Expert
                             <HelpTooltip content="The designated technical engineer/expert from the energy agency. E.g. 'John A. Johnson, PE'" />
                           </FormLabel>
                           <FormControl><Input {...field} /></FormControl>
                           <FormMessage />
                         </FormItem>
                      )}/>
                    </div>

                    <div className="col-span-1 md:col-span-2 space-y-4 pt-4 border-t mt-4">
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center text-base">
                          Organizational Chart / Direct Reports
                          <HelpTooltip content="Map out the cross-functional energy team reporting below the Executive Sponsor." />
                        </FormLabel>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!form.watch('executiveSponsorName')}
                          onClick={() => appendOrg({ id: crypto.randomUUID(), name: "", title: "", managerId: "sponsor" })}
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add Team Member
                        </Button>
                      </div>
                      
                      {orgFields.map((field, index) => (
                        <div key={field.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-4 items-end border p-4 rounded-md">
                          <FormField control={form.control} name={`orgChart.${index}.name` as const} render={({ field }) => (
                            <FormItem><FormLabel>Name</FormLabel><FormControl><Input placeholder="John Doe" {...field} /></FormControl><FormMessage /></FormItem>
                          )} />
                          <FormField control={form.control} name={`orgChart.${index}.title` as const} render={({ field }) => (
                            <FormItem><FormLabel>Title</FormLabel><FormControl><Input placeholder="Operations Manager" {...field} /></FormControl><FormMessage /></FormItem>
                          )} />
                          <FormField control={form.control} name={`orgChart.${index}.managerId` as const} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Reports To</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="sponsor">{form.watch('executiveSponsorName') || 'Executive Sponsor'}</SelectItem>
                                  {form.watch('orgChart')?.map((member, i) => (
                                    i !== index && member.name ? (
                                      <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                                    ) : null
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <div className="mb-2">
                            <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => removeOrg(index)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {orgFields.length === 0 && (
                        <div className="text-sm text-muted-foreground italic p-4 border rounded-md text-center bg-gray-50/50">
                          {form.watch('executiveSponsorName') 
                            ? "No additional team members added. Click the button above to add direct reports." 
                            : "Enter an Executive Sponsor name first to start building the organization chart."}
                        </div>
                      )}
                    </div>

                    {form.watch('executiveSponsorName') && form.watch('executiveSponsorName').length > 0 && (
                      <div className="col-span-1 md:col-span-2 space-y-4 pt-4 border-t mt-4">
                         <h4 className="font-semibold text-base mb-1">Organization Chart Preview</h4>
                         <p className="text-sm text-muted-foreground mb-4">This visual hierarchy will be generated into the final proposal output.</p>
                         <OrgChart 
                            sponsorName={form.watch("executiveSponsorName")}
                            sponsorTitle={form.watch("executiveSponsorTitle")}
                            orgData={form.watch("orgChart") || []}
                         />
                      </div>
                    )}

                    <div>
                      <FormLabel className="mb-3 flex items-center">
                        Potential Project Opportunities
                        <HelpTooltip content="Select the types of projects to include in the OsEM scope (e.g. intelligent HVAC, walk-in refrigeration). These will map to the final tasks." />
                      </FormLabel>
                      <div className="grid grid-cols-1 gap-2 border p-4 rounded-md">
                        {POTENTIAL_PROJECTS.map((item) => (
                          <FormField
                            key={item}
                            control={form.control}
                            name="potentialProjects"
                            render={({ field }) => {
                              return (
                                <FormItem key={item} className="flex flex-row items-start space-x-3 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(item)}
                                      onCheckedChange={(checked) => {
                                        return checked
                                          ? field.onChange([...field.value, item])
                                          : field.onChange(field.value?.filter((value) => value !== item));
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal">{item}</FormLabel>
                                </FormItem>
                              );
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="col-span-1 md:col-span-2 space-y-4 pt-4 border-t mt-8">
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center text-base">
                          Additional Custom Tasks
                          <HelpTooltip content="Define additional specific tasks or deliverables (e.g. Enterprise RD integration) that should be budgeted and included in the Scope of Work." />
                        </FormLabel>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => appendTask({ title: "", description: "", hours: 0 })}
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add Task
                        </Button>
                      </div>
                      
                      {taskFields.map((field, index) => (
                        <div key={field.id} className="p-4 border rounded-md space-y-4 relative bg-slate-50/50">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            className="absolute top-2 right-2 text-destructive"
                            onClick={() => removeTask(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          
                          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 pr-8">
                            <FormField
                              control={form.control}
                              name={`additionalTasks.${index}.title` as const}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Task Title</FormLabel>
                                  <FormControl><Input placeholder="e.g. Enterprise RD Integration" {...field} /></FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`additionalTasks.${index}.hours` as const}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Est. Hours</FormLabel>
                                  <FormControl><Input type="number" className="w-24" placeholder="40" {...field} /></FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          
                          <FormField
                            control={form.control}
                            name={`additionalTasks.${index}.description` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Detailed Description</FormLabel>
                                <FormControl>
                                  <Textarea 
                                    className="min-h-[80px]"
                                    placeholder="Describe specifically what will be accomplished under this task..." 
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      ))}
                      
                      {taskFields.length === 0 && (
                        <div className="text-sm text-muted-foreground italic p-4 border rounded-md text-center bg-gray-50/50">
                          No custom tasks added. Click the button above to manually define specific deliverables and labor hours.
                        </div>
                      )}
                    </div>

                    <div className="col-span-1 md:col-span-2 space-y-4 pt-4 border-t mt-8">
                      <div className="space-y-1">
                        <FormLabel className="flex items-center text-base">
                          Standard Task Deliverables (Attachment C Section 4)
                          <HelpTooltip content="Define the exact deliverables that should be generated for each standard NYSERDA task. Grok will include these in the final proposal Scope of Work." />
                        </FormLabel>
                        <p className="text-xs text-muted-foreground">Customize the deliverables that the OsEM will submit to NYSERDA for each milestone.</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/50 p-4 border rounded-md">
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task1"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 1: Project Initiation & Team Formation</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Team Charter" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task2"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 2: Energy Management Plan (EMP) Development</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Draft EMP and Final EMP" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task3"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 3: Utility Tracking & Submetering Integration</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Configured energy dashboard" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task4"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 4: ASHRAE Level II Walkthrough Audits</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Audit Report with 20+ ECMs" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task5"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 5: IPMVP-Compliant M&V Plan Development</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Baseline & M&V Report" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task6"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 6: Technical & Economic Feasibility Studies</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Feasibility reports" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task7"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 7: Operational & Maintenance (O&M) Training</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Staff certifications" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task8"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 8: ${form.watch("potentialProjects.0") || "Operational Improvement 1"}</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Implementation verification report" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task9"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 9: ${form.watch("potentialProjects.1") || "Operational Improvement 2"}</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Implementation verification report" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task10"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex justify-between items-center mb-1">
                                <FormLabel className="text-xs font-semibold text-muted-foreground">Task 10: Glidepath Transition Execution</FormLabel>
                                {proposalId && (
                                  <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0 text-xs text-primary underline"
                                    onClick={() => setLocation(`/osem-onboarding/${proposalId}/glidepath`)}
                                  >
                                    Configure Glidepath Plan
                                  </Button>
                                )}
                              </div>
                              <FormControl><Input {...field} placeholder="e.g. Handover protocol documentation" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="taskDeliverables.task11"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-semibold text-muted-foreground">Task 11: Final Reporting & Public Presentation</FormLabel>
                              <FormControl><Input {...field} placeholder="e.g. Case study and presentation slides" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="col-span-1 md:col-span-2 space-y-4 pt-4 border-t mt-8">
                       <div className="flex items-center justify-between mb-4">
                         <div className="space-y-1">
                           <FormLabel className="flex items-center text-lg font-semibold">
                             Attachment A: Project Schedule (Gantt)
                             <HelpTooltip content="Define the schedule of objectives and tasks across the timeline. Grok will auto-fill this based on the OsEM format, but you can edit it here." />
                           </FormLabel>
                           <p className="text-sm text-muted-foreground">Map deliverables and tasks to project months.</p>
                         </div>
                         <div className="flex items-center gap-4">
                           <Button
                             type="button"
                             variant="secondary"
                             size="sm"
                             onClick={downloadAttachmentA}
                             disabled={scheduleCategories.length === 0}
                           >
                             <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Schedule
                           </Button>
                           <label className="cursor-pointer">
                             <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUploadSchedule} />
                             <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3">
                               <Upload className="h-4 w-4 mr-2" /> Upload Excel
                             </div>
                           </label>
                           <FormField
                             control={form.control}
                             name="durationMonths"
                             render={({ field }) => (
                               <FormItem className="flex items-center gap-2 space-y-0">
                                 <FormLabel className="text-sm font-normal">Duration (Months):</FormLabel>
                                 <FormControl>
                                   <Input type="number" min={1} max={18} className="w-20 h-8" {...field} />
                                 </FormControl>
                               </FormItem>
                             )}
                           />
                           <Button
                             type="button"
                             variant="outline"
                             size="sm"
                             onClick={() => appendScheduleCategory({ category: "", tasks: [{ description: "", months: [] }] })}
                           >
                             <Plus className="h-4 w-4 mr-2" /> Add Category
                           </Button>
                         </div>
                       </div>

                       <div className="space-y-6 overflow-x-auto pb-4">
                         {scheduleCategories.map((field, index) => (
                           <div key={field.id} className="min-w-[800px]">
                             <ScheduleCategoryItem
                               control={form.control}
                               categoryIndex={index}
                               removeCategory={() => removeScheduleCategory(index)}
                               durationMonths={form.watch("durationMonths") || 12}
                             />
                           </div>
                         ))}

                         {scheduleCategories.length === 0 && (
                           <div className="text-sm text-muted-foreground italic p-8 border rounded-md text-center bg-gray-50/50">
                             No schedule categories added yet. Grok will automatically generate this when synthesizing the proposal from NYSERDA templates.
                           </div>
                         )}
                       </div>
                    </div>

                  </CardContent>
                </Card>

                {/* Section 4: Budget & Financial Planning (Attachment A) */}
                <Card>
                  <CardHeader>
                    <CardTitle>4. Budget & Financial (Attachment A)</CardTitle>
                    <CardDescription>Track efforts, rates, and cost share for Spreadsheets A and B.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="hourlyRate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Energy Manager Hourly Rate ($)</FormLabel>
                            <FormControl><Input type="number" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="nyserdaCostSharePercentage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>NYSERDA Cost Share (%)</FormLabel>
                            <FormControl><Input type="number" max="100" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                      <FormField
                        control={form.control}
                        name="weeklyHours"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center">
                              Weekly Hours Commitment
                              <HelpTooltip content="The expected weekly hours the manager will dedicate to the facility. NYSERDA requires a minimum of 20 hours/week." />
                            </FormLabel>
                            <FormControl>
                              <Input type="number" min="20" max="40" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="onSitePercentage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center">
                              On-Site Presence (%)
                              <HelpTooltip content="The percentage of work hours spent physically on-site at the facility. Higher physical presence is preferred." />
                            </FormLabel>
                            <FormControl>
                              <Input type="number" min="0" max="100" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                      <FormField
                        control={form.control}
                        name="travelBudget"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Travel Budget ($)</FormLabel>
                            <FormControl><Input type="number" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="materialsBudget"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Materials & Software Budget ($)</FormLabel>
                            <FormControl><Input type="number" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="pt-8 border-t space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="space-y-1">
                          <FormLabel className="flex items-center text-lg font-semibold">
                            Budget Tracker
                            <HelpTooltip content="Define the detailed budget line items here. Grok will auto-generate base items, but you can explicitly map out hours for NYSERDA forms." />
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">Estimate hours per rate for deliverables.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={downloadBudgetExcel}
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Budget
                          </Button>
                          <label className="cursor-pointer">
                            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUploadBudget} />
                            <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3">
                              <Upload className="h-4 w-4 mr-2" /> Upload Excel
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      <div className="overflow-x-auto pb-4">
                        <div className="min-w-[800px]">
                          <BudgetTaskTable control={form.control} watch={form.watch} />
                        </div>
                      </div>
                      
                      <InvoiceCyclesManager control={form.control} watch={form.watch} form={form} />
                    </div>
                  </CardContent>
                </Card>

                {proposalId && (
                  <FacilityAnalysesManager 
                    proposalId={proposalId}
                    ecmFindings={form.watch("ecmFindings") || []}
                    onUpdateFindings={(newFindings) => {
                      form.setValue("ecmFindings", newFindings, { shouldDirty: true, shouldValidate: true });
                    }}
                  />
                )}

                <div className="flex flex-wrap items-center justify-between gap-4 w-full border-t pt-4">
                  {proposalId && (
                    <div className="flex items-center gap-2 bg-muted/40 px-3 py-1.5 rounded-lg border">
                      <Label htmlFor="proposalStatusSelect" className="text-xs font-semibold text-muted-foreground uppercase">EMP State:</Label>
                      <Select 
                        value={existingProposal?.status || "draft"}
                        onValueChange={async (newStatus) => {
                          try {
                            await apiRequest("PUT", `/api/osem/${proposalId}`, { status: newStatus });
                            queryClient.invalidateQueries({
                              predicate: (query) =>
                                typeof query.queryKey[0] === "string" &&
                                (query.queryKey[0].startsWith("/api/osem") || query.queryKey[0].startsWith("/api/admin/osem"))
                            });
                            toast({
                              title: "State Updated",
                              description: `EMP status has been updated to "${newStatus}".`
                            });
                          } catch (err: any) {
                            toast({
                              title: "Update Failed",
                              description: err.message,
                              variant: "destructive"
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 w-[140px] text-xs font-medium bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="revision">Revision</SelectItem>
                          <SelectItem value="resubmitted">Resubmitted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex gap-3 ml-auto">
                    {proposalId && user?.role === "Admin" && existingProposal?.status !== "approved" && (
                      <Button 
                        type="button" 
                        variant="default" 
                        className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={approveProposalMutation.isPending} 
                        onClick={() => {
                          if (confirm("Are you sure you want to approve this onboarding proposal? This will lock the proposal text and graduate it to the active Energy Management Plan (EMP).")) {
                            approveProposalMutation.mutate(proposalId);
                          }
                        }}
                      >
                        {approveProposalMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                        )}
                        Approve Proposal (Promote to EMP)
                      </Button>
                    )}
                    
                    {proposalId && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        disabled={createProposalMutation.isPending || updateProposalMutation.isPending} 
                        onClick={form.handleSubmit((d) => onSaveDraft(d, true))}
                      >
                        {updateProposalMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        Save & Update Version
                      </Button>
                    )}

                    <Button 
                      type="button" 
                      variant="outline" 
                      disabled={createProposalMutation.isPending || updateProposalMutation.isPending} 
                      onClick={form.handleSubmit((d) => onSaveDraft(d, false))}
                    >
                      {updateProposalMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Save Draft
                    </Button>

                    <Button type="submit" disabled={createProposalMutation.isPending || generateLLMMutation.isPending}>
                      {createProposalMutation.isPending || generateLLMMutation.isPending ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing Data...</>
                      ) : (
                        <><Send className="mr-2 h-4 w-4" /> Process & Generate Proposal</>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>

          <div className="md:col-span-1">
            <div className="sticky top-6 flex flex-col gap-4">
              <Card className="max-h-[calc(100vh-16rem)] flex flex-col overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Generated Proposal
                    </CardTitle>
                    <CardDescription>
                      The formulated NYSERDA Attachment B & C structure will appear here.
                    </CardDescription>
                  </div>
                  {versions && versions.length > 0 && (
                    <Select 
                      value={selectedVersionId ? selectedVersionId.toString() : "current"} 
                      onValueChange={handleVersionSelect}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current">Current: Version {existingProposal?.version} ({existingProposal?.status})</SelectItem>
                        {versions.map(v => (
                          <SelectItem key={v.id} value={v.id.toString()}>
                            Version {v.version} ({v.status || "draft"}) — {format(new Date(v.createdAt), "MMM d, h:mm a")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto">
                  {generateLLMMutation.isPending ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground space-y-4">
                      <Loader2 className="h-8 w-8 animate-spin" />
                      <p className="text-sm">Grok is synthesizing the proposal...</p>
                    </div>
                  ) : generatedProposal ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap font-serif">
                      {generatedProposal}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground border-2 border-dashed rounded-lg p-6">
                      <p className="text-center text-sm">
                        Submit the form to generate a fully fleshed out OsEM proposal narrative using Grok.
                      </p>
                    </div>
                  )}
                </CardContent>
                {generatedProposal && (
                  <CardFooter className="bg-muted/50 mt-auto border-t p-4 flex justify-between shrink-0">
                    <span className="text-xs text-muted-foreground self-center">Updated via Grok Iteration Loop</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={downloadMarkdown}>
                        <Download className="mr-2 h-4 w-4" /> .md
                      </Button>
                      <Button variant="outline" size="sm" onClick={downloadDoc}>
                        <Download className="mr-2 h-4 w-4" /> .doc
                      </Button>
                      <Button variant="outline" size="sm" onClick={downloadExcel}>
                        <FileSpreadsheet className="mr-2 h-4 w-4" /> .xlsx
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(generatedProposal)}>
                        Copy
                      </Button>
                    </div>
                  </CardFooter>
                )}
              </Card>

              {generatedProposal && proposalId && (
                <Card className="shrink-0 bg-primary/5 border-primary/20">
                  <CardContent className="p-4 flex flex-col gap-2">
                    <label className="flex items-center text-sm font-semibold text-primary">
                      <Wand2 className="h-4 w-4 mr-2" /> Ask Grok to Revise
                    </label>
                    <Textarea 
                      placeholder="e.g. Expand the glidepath training portion, or increase the electric target to 15%."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      className="resize-none min-h-[80px]"
                    />
                    <Button 
                      size="sm" 
                      className="w-full mt-2" 
                      disabled={!feedback.trim() || reviseLLMMutation.isPending}
                      onClick={() => reviseLLMMutation.mutate({ id: proposalId, feedback })}
                    >
                      {reviseLLMMutation.isPending ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Grok is revising...</>
                      ) : (
                        "Submit Feedback"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  </ErrorBoundary>
  );
}
